import { API_URL } from './config';
import {
  Order,
  OrderStatus,
  Banner,
  BannerCreateRequest,
  BannerFilter,
  BannerStatistics,
  Offer,
  OfferCreateRequest,
  OfferFilter,
  OfferStatistics,
  FeaturedDeal,
  FeaturedDealCreateRequest,
  FeaturedDealFilter,
  FeaturedDealStatistics
} from './types';

// API Response interfaces
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  meta: any;
}

// Generic Pagination interface
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Specific supplier response interface
export interface SupplierPaginatedResponse {
  suppliers: Supplier[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Enhanced transporter response interface
export interface TransporterPaginatedResponse {
  transporters: EnhancedTransporter[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Transporter route response interface
export interface TransporterRoutePaginatedResponse {
  routes: TransporterRoute[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Order response interface
export interface OrderPaginatedResponse {
  orders: Order[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// User and Authentication interfaces
export interface User {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'super-admin' | 'supplier';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

// Supplier interfaces
export interface Supplier {
  _id: string;
  businessName: string;
  ownerName: string;
  email: string;
  mobile: string;
  isActive: boolean;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  gstNumber?: string;
  panNumber?: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
  };
  products?: string[];
  documents?: Array<{
    name: string;
    url: string;
    type: string;
    uploadedAt: string;
  }>;
  profilePicture?: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierCreateRequest {
  businessName: string;
  ownerName: string;
  email: string;
  mobile: string;
  password: string;
  gstNumber?: string;
  panNumber?: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
  };
}

export interface SupplierUpdateRequest {
  businessName?: string;
  ownerName?: string;
  email?: string;
  mobile?: string;
  gstNumber?: string;
  panNumber?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
  };
  verificationStatus?: 'pending' | 'verified' | 'rejected';
  adminNotes?: string;
}

export interface SupplierStats {
  totalSuppliers: number;
  activeSuppliers: number;
  verifiedSuppliers: number;
  pendingVerification: number;
  rejectedSuppliers: number;
  supplierGrowth: {
    period: string;
    growth: number;
    previousCount: number;
    currentCount: number;
  };
  topSuppliers: Array<{
    supplierId: string;
    businessName: string;
    totalOrders: number;
    totalRevenue: number;
    rating: number;
  }>;
  verificationStats: {
    pending: number;
    verified: number;
    rejected: number;
  };
}

export interface SupplierInventory {
  supplierId: string;
  businessName: string;
  products: Array<{
    productId: string;
    name: string;
    currentStock: number;
    reservedStock: number;
    availableStock: number;
    lowStockThreshold: number;
    reorderPoint: number;
    isLowStock: boolean;
    isOutOfStock: boolean;
  }>;
  totalStock: number;
  totalReserved: number;
  totalAvailable: number;
  lowStockCount: number;
  outOfStockCount: number;
}

export interface SupplierLocation {
  _id: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  coordinates: {
    latitude: number;
    longitude: number;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierLocationCreateRequest {
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

export interface SupplierLocationUpdateRequest extends Partial<SupplierLocationCreateRequest> {
  isActive?: boolean;
}

// Admin Transporter interfaces for enhanced management
export interface TransporterStats {
  totalTransporters: number;
  activeTransporters: number;
  verifiedTransporters: number;
  pendingVerification: number;
  rejectedTransporters: number;
  transporterGrowth: {
    period: string;
    growth: number;
    previousCount: number;
    currentCount: number;
  };
  topTransporters: Array<{
    transporterId: string;
    name: string;
    totalTrips: number;
    totalRevenue: number;
    rating: number;
    completedTrips: number;
  }>;
  verificationStats: {
    pending: number;
    verified: number;
    rejected: number;
  };
  vehicleStats: {
    totalVehicles: number;
    activeVehicles: number;
    expiredInsurance: number;
    documentsExpiring: number;
  };
}

export interface TransporterFleet {
  transporterId: string;
  name: string;
  vehicles: Array<{
    _id: string;
    registrationNumber: string;
    type: string;
    capacity: {
      value: number;
      unit: string;
    };
    insuranceExpiryDate: string;
    isActive: boolean;
    currentLocation?: {
      latitude: number;
      longitude: number;
      address: string;
      lastUpdated: string;
    };
    status: 'available' | 'in_transit' | 'maintenance' | 'inactive';
    documents: Array<{
      name: string;
      type: string;
      fileUrl: string;
      expiryDate?: string;
      uploadedAt: string;
    }>;
  }>;
  drivers: Array<{
    _id: string;
    name: string;
    mobile: string;
    licenseNumber: string;
    licenseExpiry: string;
    isActive: boolean;
    currentStatus: 'available' | 'on_trip' | 'break' | 'inactive';
  }>;
  totalVehicles: number;
  activeVehicles: number;
  totalDrivers: number;
  activeDrivers: number;
}

export interface TransporterRoute {
  _id: string;
  transporterId: string;
  origin: {
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  destination: {
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  distance: number;
  estimatedTime: number;
  actualTime?: number;
  routePoints: Array<{
    latitude: number;
    longitude: number;
    timestamp: string;
  }>;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface TransporterAnalytics {
  transporterId: string;
  name: string;
  performance: {
    totalTrips: number;
    completedTrips: number;
    cancelledTrips: number;
    completionRate: number;
    averageRating: number;
    totalRevenue: number;
    onTimeDeliveryRate: number;
    averageDeliveryTime: number;
  };
  monthlyStats: Array<{
    month: string;
    trips: number;
    revenue: number;
    rating: number;
  }>;
  vehicleUtilization: Array<{
    vehicleId: string;
    registrationNumber: string;
    utilizationRate: number;
    tripsCompleted: number;
    maintenanceHours: number;
  }>;
  routeAnalytics: {
    mostCommonRoutes: Array<{
      origin: string;
      destination: string;
      count: number;
      averageTime: number;
    }>;
    distanceCovered: number;
    fuelEfficiency: number;
  };
}

// Enhanced Transporter interface with admin fields
export interface EnhancedTransporter extends Transporter {
  adminNotes?: string;
  performanceMetrics?: {
    totalTrips: number;
    completedTrips: number;
    cancelledTrips: number;
    averageRating: number;
    onTimeDeliveryRate: number;
  };
  fleet?: TransporterFleet;
  currentLocation?: {
    latitude: number;
    longitude: number;
    address: string;
    lastUpdated: string;
  };
}

// Enhanced Transporter Update Request
export interface EnhancedTransporterUpdateRequest extends TransporterUpdateRequest {
  adminNotes?: string;
  rejectionReason?: string;
}

// Category types
interface Category {
  _id: string;
  name: string;
  description: string;
  image: string;
  isActive: boolean;
  parent: string | null;
  level: number;
  useCalculationLogic: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface CategoryCreateRequest {
  name: string;
  description: string;
  parent?: string;
  useCalculationLogic?: boolean;
}

interface CategoryUpdateRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
  parent?: string;
  useCalculationLogic?: boolean;
}

// Category Pricing Configuration interfaces
export interface CategoryPricingConfig {
  perKmRate: number;
  minimumCharge: number;
  maximumCharge: number | null;
  fuelSurchargePercentage: number;
  rushHourMultiplier: number;
  isCustomConfig?: boolean;
  lastUpdatedAt?: string;
  lastUpdatedBy?: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface CategoryPricingConfigResponse {
  category: {
    _id: string;
    name: string;
    useCalculationLogic: boolean;
  };
  hasCustomConfig: boolean;
  activeConfig: 'category' | 'global';
  categoryConfig: CategoryPricingConfig | null;
  globalConfig: CategoryPricingConfig;
  effectiveConfig: CategoryPricingConfig & { source: 'category' | 'global' };
}

export interface PricingConfigUpdateRequest {
  perKmRate: number;
  minimumCharge?: number;
  maximumCharge?: number | null;
  fuelSurchargePercentage?: number;
  rushHourMultiplier?: number;
}

export interface PricingTestRequest {
  distance: number;
  quantity?: number;
  materialPrice?: number;
}

export interface PricingTestResponse {
  inputs: {
    distance: number;
    quantity: number;
    materialPrice: number;
  };
  configUsed: {
    source: 'category' | 'global';
    perKmRate: number;
    minimumCharge: number;
    maximumCharge: number | null;
    fuelSurchargePercentage: number;
    rushHourMultiplier: number;
  };
  calculation: {
    baseTransportFee: number;
    fuelSurcharge: number;
    rushHourAdjustment: number;
    transportFeeBeforeCaps: number;
    transportFeeAfterCaps: number;
    cappedBy: 'minimumCharge' | 'maximumCharge' | null;
    materialFeeTotal: number;
    transportFeeTotal: number;
    totalPrice: number;
    perUnit: {
      materialFee: number;
      transportFee: number;
      totalPrice: number;
    };
  };
  formula: {
    step1: string;
    step2: string;
    step3: string;
    step4: string;
    step5: string;
  };
}

// Product interfaces
export interface Product {
  _id: string;
  name: string;
  description?: string;
  category: string | Category;
  unit: string;
  basePrice: number;
  taxRate: number;
  discount: number;
  minOrderQuantity: number;
  maxOrderQuantity: number;
  isActive: boolean;
  images: string[];
  defaultImage: string;
  specifications: Array<{
    name: string;
    value: string;
  }>;
  suppliers?: Array<{
    supplier: string | {
      _id: string;
      name: string;
    };
    price: number;
    isActive: boolean;
    locations?: Array<{
      mineLocation: string;
      stock: number;
    }>;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface ProductCreateRequest {
  name: string;
  description?: string;
  category: string;
  unit: string;
  basePrice: number;
  taxRate?: number;
  discount?: number;
  minOrderQuantity?: number;
  maxOrderQuantity?: number;
  isActive?: boolean;
  specifications?: Array<{
    name: string;
    value: string;
  }>;
}

export interface ProductUpdateRequest extends Partial<ProductCreateRequest> { }

// Product unit options
export const ProductUnitOptions = {
  ton: 'Ton',
  kg: 'Kilogram',
  cubic_meters: 'Cubic Meters',
  bags: 'Bags',
  pieces: 'Pieces'
};

export type ProductUnitType = keyof typeof ProductUnitOptions;

// Pricing interfaces
export interface ProductPricing {
  _id: string;
  name: string;
  description?: string;
  category: {
    _id: string;
    name: string;
  };
  basePrice: number;
  sellingPrice: number;
  taxRate: number;
  discount: number;
  unit: string;
  isActive: boolean;
  suppliers?: Array<{
    _id: string;
    name: string;
    price: number;
    lastUpdated: string;
  }>;
  avgSupplierPrice?: number;
  priceHistoryCount: number;
  margin: string;
  calculatedSellingPrice?: number;
  effectiveDiscount?: {
    discountPercentage: number;
    type: 'permanent' | 'timed';
  };
  timeBasedDiscounts?: Array<{
    _id: string;
    effectiveFrom: string;
    effectiveTo: string;
    discountPercentage: number;
    isActive: boolean;
    reason?: string;
  }>;
}

export interface PricingPaginatedResponse {
  products: ProductPricing[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface PriceHistory {
  _id: string;
  product: string;
  basePrice: number;
  sellingPrice: number;
  taxRate: number;
  discount: number;
  effectiveDate: string;
  reason: string;
  updatedBy: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface PriceHistoryPaginatedResponse {
  priceHistory: PriceHistory[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface TimeBasedDiscount {
  _id: string;
  product: string;
  discountPercentage: number;
  effectiveFrom: string;
  effectiveTo: string;
  reason: string;
  isActive: boolean;
  createdBy: string | {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DiscountsResponse {
  discounts: TimeBasedDiscount[];
  activeDiscount: TimeBasedDiscount | null;
  currentDiscount: number;
  discountType: 'permanent' | 'timed';
}

// Inventory Interfaces
export interface InventoryDashboard {
  totalProducts: number;
  totalSuppliers: number;
  productsWithInventory: number;
  lowStockCount: number;
  outOfStockCount: number;
  inventoryStats: {
    totalStock: number;
    totalReserved: number;
    totalAvailable: number;
  };
  recentTransactions: Array<{
    _id: string;
    product: {
      _id: string;
      name: string;
      sku: string;
    };
    supplier: {
      _id: string;
      name: string;
    };
    type: string;
    quantity: number;
    previousStock: number;
    newStock: number;
    createdAt: string;
    performedBy: {
      _id: string;
      name: string;
      email: string;
    };
  }>;
  topSuppliers: Array<{
    _id: string;
    name: string;
    totalStock: number;
    productsCount: number;
  }>;
  inventoryByCategory: Array<{
    _id: string;
    name: string;
    totalStock: number;
    productsCount: number;
  }>;
}

export interface InventoryProduct {
  _id: string;
  name: string;
  sku: string;
  description: string;
  category: {
    _id: string;
    name: string;
  };
  basePrice: number;
  isActive: boolean;
  totalStock: number;
  totalReserved: number;
  totalAvailable: number;
  suppliersCount: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  inventoryDetails: Array<{
    supplierId: string;
    supplierName: string;
    currentStock: number;
    reservedStock: number;
    availableStock: number;
    lowStockThreshold: number;
    reorderPoint: number;
  }>;
}

export interface InventoryProductsResponse {
  products: InventoryProduct[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface InventoryCategoryResponse {
  category: {
    _id: string;
    name: string;
  };
  products: InventoryProduct[];
  categoryTotals: {
    totalProducts: number;
    totalStock: number;
    totalReserved: number;
    totalAvailable: number;
    supplierCount: number;
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface InventorySupplier {
  _id: string;
  name: string;
  businessName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  isActive: boolean;
  inventoryStats: {
    productsCount: number;
    totalStock: number;
    totalReserved: number;
    totalAvailable: number;
    lowStockCount: number;
    outOfStockCount: number;
  };
  lastUpdated: string;
}

export interface InventorySuppliersResponse {
  suppliers: InventorySupplier[];
  overallStats: {
    totalProducts: number;
    totalStock: number;
    totalReserved: number;
    totalAvailable: number;
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface InventorySupplierDetailedResponse {
  supplier: {
    _id: string;
    name: string;
    email: string;
    phone: string;
  };
  products: Array<{
    product: {
      _id: string;
      name: string;
      sku: string;
      description: string;
      category: {
        _id: string;
        name: string;
      };
      basePrice: number;
      isActive: boolean;
    };
    price: number;
    inventory: {
      currentStock: number;
      reservedStock: number;
      availableStock: number;
      lowStockThreshold: number;
      reorderPoint: number;
      isLowStock: boolean;
      isOutOfStock: boolean;
    };
  }>;
  inventoryStats: {
    totalProducts: number;
    totalStock: number;
    totalReserved: number;
    totalAvailable: number;
    lowStockCount: number;
    outOfStockCount: number;
  };
  recentTransactions: Array<{
    _id: string;
    product: {
      _id: string;
      name: string;
    };
    type: string;
    quantity: number;
    previousStock: number;
    newStock: number;
    createdAt: string;
    performedBy: {
      _id: string;
      name: string;
      email: string;
    };
  }>;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Transporter interfaces
export interface Transporter {
  _id: string;
  name: string;
  email: string;
  mobile: string;
  alternatePhone?: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  drivingLicenseNumber?: string;
  drivingLicenseExpiry?: string;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  rejectionReason?: string;
  isActive: boolean;
  rating?: number;
  completedTrips?: number;
  vehicles?: Array<{
    _id: string;
    registrationNumber: string;
    type: string;
    capacity: {
      value: number;
      unit: string;
    };
    insuranceExpiryDate: string;
    documents?: Array<{
      _id: string;
      name: string;
      type: string;
      fileUrl: string;
      uploadedAt: string;
    }>;
    isActive: boolean;
  }>;
  drivers?: Array<{
    _id: string;
    name: string;
    mobile: string;
    licenseNumber: string;
    licenseExpiry: string;
    documents?: Array<{
      _id: string;
      name: string;
      type: string;
      fileUrl: string;
      uploadedAt: string;
    }>;
    isActive: boolean;
  }>;
  documents?: Array<{
    _id: string;
    name: string;
    type: string;
    fileUrl: string;
    uploadedAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface TransporterCreateRequest {
  name: string;
  email: string;
  password: string;
  mobile: string;
  alternatePhone?: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  drivingLicenseNumber?: string;
  drivingLicenseExpiry?: string;
}

export interface TransporterUpdateRequest {
  name?: string;
  mobile?: string;
  alternatePhone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
  };
  verificationStatus?: 'pending' | 'verified' | 'rejected';
  isActive?: boolean;
}

export interface TransporterVehicleRequest {
  registrationNumber: string;
  type: string;
  capacity: {
    value: number;
    unit: string;
  };
  insuranceExpiryDate: string;
}

export interface TransporterDriverRequest {
  name: string;
  mobile: string;
  licenseNumber: string;
  licenseExpiry: string;
}

export interface PaginatedTransporterResponse {
  transporters: Transporter[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

// Discount interfaces
export interface Discount {
  _id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed' | 'shipping';
  value: number;
  minCartValue: number;
  maxDiscountAmount?: number;
  validFrom: string;
  validTo: string;
  usageLimit?: number;
  perUserLimit: number;
  totalUses: number;
  appliesTo: 'all' | 'categories' | 'products';
  categories: string[];
  products: string[];
  firstTimeUsers: boolean;
  isActive: boolean;
  isCurrentlyValid: boolean;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DiscountCreateRequest {
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed' | 'shipping';
  value: number;
  minCartValue?: number;
  maxDiscountAmount?: number;
  validFrom: string;
  validTo: string;
  usageLimit?: number;
  perUserLimit?: number;
  appliesTo?: 'all' | 'categories' | 'products';
  categories?: string[];
  products?: string[];
  firstTimeUsers?: boolean;
}

export interface DiscountUpdateRequest extends Partial<DiscountCreateRequest> {
  isActive?: boolean;
}

export interface DiscountPaginatedResponse {
  discounts: Discount[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Notification interfaces
export interface Notification {
  _id: string;
  title: string;
  body: string;
  image?: string;
  targetType: 'all' | 'specific' | 'category' | 'location';
  targetCategory?: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
  targetUsers?: string[];
  locationFilter?: {
    cities?: string[];
    states?: string[];
    pincodes?: string[];
  };
  data?: Record<string, any>;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
  totalTargeted: number;
  totalSent: number;
  totalFailed: number;
  totalDelivered?: number;
  clickCount?: number;
  openCount?: number;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  sentAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationTemplate {
  _id: string;
  name: string;
  description: string;
  category: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
  type: 'manual' | 'automatic' | 'scheduled';
  title: string;
  body: string;
  image?: string;
  variables: Array<{
    name: string;
    description: string;
    required: boolean;
    defaultValue?: string;
  }>;
  defaultTargeting: {
    targetType: 'all' | 'specific' | 'category' | 'location';
    targetCategory?: string;
  };
  triggers?: {
    events: Array<{
      type: string;
      conditions?: Record<string, any>;
    }>;
  };
  isActive: boolean;
  priority: number;
  usage: {
    timesUsed: number;
    totalSent: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface NotificationDashboard {
  overview: {
    totalNotifications: number;
    deliveryStats: {
      totalSent: number;
      totalTargeted: number;
      totalFailed: number;
      totalDelivered: number;
      avgDeliveryRate: number;
    };
  };
  charts: {
    daily: Array<{
      _id: string;
      count: number;
      totalSent: number;
      totalTargeted: number;
      totalFailed: number;
    }>;
    categoryBreakdown: Array<{
      _id: string;
      count: number;
      totalSent: number;
      totalTargeted: number;
    }>;
    templateUsage: Array<{
      _id: string;
      count: number;
      totalUsed: number;
      totalSent: number;
    }>;
  };
  userStats: {
    totalUsers: number;
    usersWithFCM: number;
    activeUsers: number;
  };
  period: string;
}

export interface NotificationPreview {
  targetCount: number;
  targeting: {
    type: string;
    category?: string;
    locationFilter?: Record<string, any>;
  };
  locationBreakdown?: Record<string, number>;
  estimatedDelivery: number;
}

export interface NotificationUserStats {
  totalUsers: number;
  usersWithFCM: number;
  activeUsers: number;
  fcmCoverage: string;
  notificationPreferences: Record<string, number>;
  topStates: Array<{
    _id: string;
    count: number;
  }>;
}

export interface NotificationCreateRequest {
  title: string;
  body: string;
  image?: string;
  targetType: 'all' | 'specific' | 'category' | 'location';
  targetUsers?: string[];
  targetCategory?: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
  locationFilter?: {
    cities?: string[];
    states?: string[];
    pincodes?: string[];
  };
  data?: Record<string, any>;
}

export interface NotificationTemplateCreateRequest {
  name: string;
  description: string;
  category: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
  type: 'manual' | 'automatic' | 'scheduled';
  title: string;
  body: string;
  image?: string;
  variables: Array<{
    name: string;
    description: string;
    required: boolean;
    defaultValue?: string;
  }>;
  defaultTargeting: {
    targetType: 'all' | 'specific' | 'category' | 'location';
    targetCategory?: string;
  };
  triggers?: {
    events: Array<{
      type: string;
      conditions?: Record<string, any>;
    }>;
  };
  isActive?: boolean;
  priority?: number;
}

export interface NotificationPaginatedResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  summary?: {
    totalNotifications: number;
    totalTargeted: number;
    totalSent: number;
    totalFailed: number;
    avgDeliveryRate: number;
  };
}

export interface NotificationTemplatePaginatedResponse {
  templates: NotificationTemplate[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Helper function to handle API requests with JSON data
async function fetchAPI<T = any>(endpoint: string, options = {}): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('auth_token');

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token.trim()}` } : {})
    }
  };

  try {
    console.log(`Making API request to: ${API_URL}${endpoint}`);
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...defaultOptions,
      ...options
    });

    // Check if the response is not ok
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || `API Error: ${response.status}`;
        console.error('API error response:', errorData);

        // Handle JWT expiration
        if (errorData.errors?.error === "jwt expired" || errorData.message?.includes("token failed")) {
          console.error("Authentication token expired. Redirecting to login...");
          // Clear local storage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');

          // Redirect to login page
          window.location.href = '/login';
          return Promise.reject(new Error("Your session has expired. Please log in again."));
        }
      } catch (parseError) {
        errorMessage = `API Error: ${response.status} - Could not parse error response`;
        console.error('Failed to parse error response:', parseError);
      }
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    console.error(`Error in API call to ${endpoint}:`, error);
    throw error;
  }
}

// Helper function to handle API requests with FormData
async function fetchFormAPI<T = any>(endpoint: string, formData: FormData, method = 'POST'): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('auth_token');

  const options = {
    method,
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token.trim()}` } : {})
    },
    body: formData
  };

  const response = await fetch(`${API_URL}${endpoint}`, options);

  // Check if the response is not ok
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error occurred' }));

    // Handle 401 Unauthorized or JWT expiration
    if (response.status === 401 ||
      errorData.errors?.error === "jwt expired" ||
      errorData.message?.includes("token failed") ||
      errorData.message?.includes("Not authorized")) {
      console.error("Authentication failed. Redirecting to login...");
      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');

      // Redirect to login page
      window.location.href = '/login';
      return Promise.reject(new Error("Your session has expired. Please log in again."));
    }

    throw new Error(errorData.message || `API Error: ${response.status}`);
  }

  return response.json();
}

// Categories API
export const CategoryAPI = {
  // Get all categories
  getAllCategories: (): Promise<ApiResponse<Category[]>> =>
    fetchAPI<Category[]>('/categories'),

  // Get a single category by ID
  getCategory: (id: string): Promise<ApiResponse<Category>> =>
    fetchAPI<Category>(`/categories/${id}`),

  // Create a new category with optional image
  createCategory: (categoryData: CategoryCreateRequest, image?: File): Promise<ApiResponse<Category>> => {
    const formData = new FormData();
    formData.append('name', categoryData.name);
    formData.append('description', categoryData.description || '');

    if (categoryData.parent) {
      formData.append('parent', categoryData.parent);
    }

    if (categoryData.useCalculationLogic !== undefined) {
      formData.append('useCalculationLogic', String(categoryData.useCalculationLogic));
    }

    if (image) {
      formData.append('categoryImage', image);
    }

    return fetchFormAPI<Category>('/categories', formData);
  },

  // Update a category with optional image
  updateCategory: (id: string, categoryData: CategoryUpdateRequest, image?: File): Promise<ApiResponse<Category>> => {
    const formData = new FormData();

    if (categoryData.name) {
      formData.append('name', categoryData.name);
    }

    if (categoryData.description !== undefined) {
      formData.append('description', categoryData.description);
    }

    if (categoryData.parent !== undefined) {
      formData.append('parent', categoryData.parent || '');
    }

    if (categoryData.useCalculationLogic !== undefined) {
      formData.append('useCalculationLogic', String(categoryData.useCalculationLogic));
    }

    if (image) {
      formData.append('categoryImage', image);
    }

    return fetchFormAPI<Category>(`/categories/${id}`, formData, 'PUT');
  },

  // Delete a category
  deleteCategory: (id: string): Promise<ApiResponse<any>> =>
    fetchAPI(`/categories/${id}`, {
      method: 'DELETE'
    }),

  // Toggle category active status
  toggleCategoryActive: (id: string): Promise<ApiResponse<Category>> =>
    fetchAPI<Category>(`/categories/${id}/toggle-active`, {
      method: 'PUT'
    }),

  // Toggle calculation logic for category
  toggleCalculationLogic: (id: string, useCalculationLogic: boolean): Promise<ApiResponse<Category>> =>
    fetchAPI<Category>(`/categories/${id}/calculation-logic`, {
      method: 'PUT',
      body: JSON.stringify({ useCalculationLogic })
    }),

  // Get pricing configuration for a category
  getPricingConfig: (categoryId: string): Promise<ApiResponse<CategoryPricingConfigResponse>> =>
    fetchAPI<CategoryPricingConfigResponse>(`/categories/${categoryId}/pricing-config`),

  // Update pricing configuration for a category
  updatePricingConfig: (categoryId: string, config: PricingConfigUpdateRequest): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/categories/${categoryId}/pricing-config`, {
      method: 'PUT',
      body: JSON.stringify(config)
    }),

  // Delete/Reset pricing configuration to global
  deletePricingConfig: (categoryId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/categories/${categoryId}/pricing-config`, {
      method: 'DELETE'
    }),

  // Test pricing calculation
  testPricingConfig: (categoryId: string, testData: PricingTestRequest): Promise<ApiResponse<PricingTestResponse>> =>
    fetchAPI<PricingTestResponse>(`/categories/${categoryId}/pricing-config/test`, {
      method: 'POST',
      body: JSON.stringify(testData)
    })
};


// Products API
export const ProductAPI = {
  // Get all products
  getAllProducts: (): Promise<ApiResponse<PaginatedResponse<Product> | Product[]>> =>
    fetchAPI<PaginatedResponse<Product> | Product[]>('/products'),

  // Get products by category ID
  getProductsByCategory: (categoryId: string): Promise<ApiResponse<PaginatedResponse<Product> | Product[]>> =>
    fetchAPI<PaginatedResponse<Product> | Product[]>(`/products?category=${categoryId}`),

  // Get a single product by ID
  getProduct: (id: string): Promise<ApiResponse<Product>> =>
    fetchAPI<Product>(`/products/${id}`),

  // Create a new product
  createProduct: (productData: ProductCreateRequest): Promise<ApiResponse<Product>> =>
    fetchAPI<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(productData)
    }),

  // Update a product
  updateProduct: (id: string, productData: ProductUpdateRequest): Promise<ApiResponse<Product>> =>
    fetchAPI<Product>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData)
    }),

  // Delete a product
  deleteProduct: (id: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/products/${id}`, {
      method: 'DELETE'
    }),

  // Toggle product active status
  toggleProductActive: async (id: string): Promise<ApiResponse<Product>> => {
    try {
      console.log(`Toggling active status for product: ${id}`);
      const response = await fetchAPI<Product>(`/products/${id}/toggle-active`, {
        method: 'PUT'
      });
      console.log('Toggle response:', response);
      return response;
    } catch (error) {
      console.error('Error in toggleProductActive:', error);
      // Re-throw to allow component to handle
      throw error;
    }
  },

  // Upload product images
  uploadProductImages: (id: string, images: File[]): Promise<ApiResponse<Product>> => {
    const formData = new FormData();

    // Append each image to the formData with the key 'images'
    for (const image of images) {
      formData.append('images', image);
    }

    return fetchFormAPI<Product>(`/products/${id}/upload-images`, formData);
  },

  // Set default product image
  setDefaultProductImage: (id: string, imageIndex: number): Promise<ApiResponse<Product>> =>
    fetchAPI<Product>(`/products/${id}/set-default-image/${imageIndex}`, {
      method: 'PUT'
    }),

  // Remove product image
  removeProductImage: (id: string, imageIndex: number): Promise<ApiResponse<Product>> =>
    fetchAPI<Product>(`/products/${id}/remove-image/${imageIndex}`, {
      method: 'DELETE'
    }),

  // Get available suppliers for a product
  getProductSuppliers: (id: string, minQuantity?: number, active?: boolean): Promise<ApiResponse<any>> => {
    const queryParams = [];
    if (minQuantity !== undefined) queryParams.push(`minQuantity=${minQuantity}`);
    if (active !== undefined) queryParams.push(`active=${active}`);

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<any>(`/products/${id}/suppliers${queryString}`);
  },

  // Update product supplier
  updateProductSupplier: (id: string, supplierData: {
    supplier: string;
    price: number;
    isActive: boolean;
    locations?: Array<{
      mineLocation: string;
      stock: number;
    }>;
  }): Promise<ApiResponse<Product>> =>
    fetchAPI<Product>(`/products/${id}/supplier`, {
      method: 'PUT',
      body: JSON.stringify(supplierData)
    }),

  // Remove product supplier
  removeProductSupplier: (id: string, supplierId: string): Promise<ApiResponse<Product>> =>
    fetchAPI<Product>(`/products/${id}/supplier/${supplierId}`, {
      method: 'DELETE'
    })
};

// Authentication API
export const AuthAPI = {
  // Login
  login: (credentials: LoginRequest): Promise<ApiResponse<{ user: User; token: string }>> =>
    fetchAPI<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    }),

  // Logout
  logout: (): Promise<ApiResponse<null>> =>
    fetchAPI<null>('/auth/logout', {
      method: 'POST'
    }),

  // Get current user
  getCurrentUser: (): Promise<ApiResponse<User>> =>
    fetchAPI<User>('/auth/me'),

  // Change password
  changePassword: (oldPassword: string, newPassword: string): Promise<ApiResponse<null>> =>
    fetchAPI<null>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ oldPassword, newPassword })
    })
};

// Suppliers API
export const SupplierAPI = {
  // Get all suppliers
  getAllSuppliers: (params?: {
    search?: string;
    verificationStatus?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<Supplier>>> => {
    const queryParams = [];
    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.verificationStatus) queryParams.push(`verificationStatus=${params.verificationStatus}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<PaginatedResponse<Supplier>>(`/suppliers${queryString}`);
  },

  // Get a single supplier by ID
  getSupplier: (id: string): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>(`/suppliers/${id}`),

  // Register a new supplier (Admin only)
  registerSupplier: (supplierData: SupplierCreateRequest): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>('/suppliers/register', {
      method: 'POST',
      body: JSON.stringify(supplierData)
    }),

  // Update a supplier (Admin only)
  updateSupplier: (id: string, supplierData: SupplierUpdateRequest): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>(`/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(supplierData)
    }),

  // Delete a supplier (Admin only)
  deleteSupplier: (id: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/suppliers/${id}`, {
      method: 'DELETE'
    }),

  // Toggle supplier active status (Admin only)
  toggleSupplierActive: (id: string): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>(`/suppliers/${id}/toggle-active`, {
      method: 'PUT'
    }),

  // Get supplier statistics (Admin only)
  getSupplierStats: (): Promise<ApiResponse<SupplierStats>> =>
    fetchAPI<SupplierStats>('/suppliers/stats'),

  // Get supplier inventory (Admin only)
  getSupplierInventory: (id: string): Promise<ApiResponse<SupplierInventory>> =>
    fetchAPI<SupplierInventory>(`/suppliers/${id}/inventory`),

  // Get supplier orders (Admin only)
  getSupplierOrders: (id: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<PaginatedResponse<Order>>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.status) queryParams.push(`status=${params.status}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<PaginatedResponse<Order>>(`/suppliers/${id}/orders${queryString}`);
  },

  // Get supplier locations (Admin only)
  getSupplierLocations: (id: string): Promise<ApiResponse<SupplierLocation[]>> =>
    fetchAPI<SupplierLocation[]>(`/suppliers/${id}/locations`),

  // Add supplier location (Admin only)
  addSupplierLocation: (id: string, locationData: SupplierLocationCreateRequest): Promise<ApiResponse<SupplierLocation>> =>
    fetchAPI<SupplierLocation>(`/suppliers/${id}/locations`, {
      method: 'POST',
      body: JSON.stringify(locationData)
    }),

  // Update supplier location (Admin only)
  updateSupplierLocation: (id: string, locationId: string, locationData: SupplierLocationUpdateRequest): Promise<ApiResponse<SupplierLocation>> =>
    fetchAPI<SupplierLocation>(`/suppliers/${id}/locations/${locationId}`, {
      method: 'PUT',
      body: JSON.stringify(locationData)
    }),

  // Delete supplier location (Admin only)
  deleteSupplierLocation: (id: string, locationId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/suppliers/${id}/locations/${locationId}`, {
      method: 'DELETE'
    }),

  // Upload business document
  uploadDocument: (supplierId: string, document: File, documentName: string, documentType: string): Promise<ApiResponse<Supplier>> => {
    const formData = new FormData();
    formData.append('document', document);
    formData.append('name', documentName);
    formData.append('type', documentType);

    return fetchFormAPI<Supplier>(`/suppliers/${supplierId}/documents`, formData);
  },

  // Delete business document
  deleteDocument: (supplierId: string, documentId: string): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>(`/suppliers/${supplierId}/documents/${documentId}`, {
      method: 'DELETE'
    }),

  // Upload profile picture
  uploadProfilePicture: (supplierId: string, image: File): Promise<ApiResponse<Supplier>> => {
    const formData = new FormData();
    formData.append('image', image);

    return fetchFormAPI<Supplier>(`/suppliers/${supplierId}/profile-picture`, formData);
  },

  // Change supplier password
  changePassword: (supplierId: string, oldPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> =>
    fetchAPI<{ message: string }>(`/suppliers/${supplierId}/change-password`, {
      method: 'POST',
      body: JSON.stringify({ oldPassword, newPassword })
    }),

  // Update supplier profile
  updateProfile: (supplierId: string, profileData: Partial<SupplierUpdateRequest>): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>(`/suppliers/${supplierId}/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData)
    })
};

// Admin Supplier Management API (New endpoints with /api/admin prefix)
export const AdminSupplierAPI = {
  // Get all suppliers with comprehensive statistics
  getAllSuppliers: (params?: {
    search?: string;
    verificationStatus?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
    includeStats?: boolean;
  }): Promise<ApiResponse<SupplierPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.verificationStatus) queryParams.push(`verificationStatus=${params.verificationStatus}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.includeStats) queryParams.push(`includeStats=${params.includeStats}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<SupplierPaginatedResponse>(`/admin/suppliers${queryString}`);
  },

  // Get comprehensive supplier details with analytics
  getSupplierById: (id: string): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>(`/admin/suppliers/${id}`),

  // Get detailed order history for a supplier
  getSupplierOrderHistory: (id: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<OrderPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.status) queryParams.push(`status=${params.status}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<OrderPaginatedResponse>(`/admin/suppliers/${id}/orders${queryString}`);
  },

  // Update supplier details and verification status
  updateSupplier: (id: string, supplierData: {
    businessName?: string;
    ownerName?: string;
    email?: string;
    verificationStatus?: 'pending' | 'verified' | 'rejected';
    isActive?: boolean;
    adminNotes?: string;
    [key: string]: any;
  }): Promise<ApiResponse<Supplier>> =>
    fetchAPI<Supplier>(`/admin/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(supplierData)
    }),

  // Delete supplier (soft delete or permanent delete)
  deleteSupplier: (id: string, options?: {
    permanent?: boolean;
    reason?: string;
  }): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/suppliers/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(options || {})
    }),

  // Get comprehensive supplier statistics
  getSupplierStatistics: (params?: {
    period?: '7d' | '30d' | '90d' | '1y';
  }): Promise<ApiResponse<{
    totalSuppliers: number;
    activeSuppliers: number;
    verifiedSuppliers: number;
    pendingVerification: number;
    rejectedSuppliers: number;
    supplierGrowth: {
      period: string;
      growth: number;
      previousCount: number;
      currentCount: number;
    };
    topSuppliers: Array<{
      supplierId: string;
      businessName: string;
      totalOrders: number;
      totalRevenue: number;
      rating: number;
    }>;
    verificationStats: {
      pending: number;
      verified: number;
      rejected: number;
    };
    [key: string]: any;
  }>> => {
    const queryParams = [];
    if (params?.period) queryParams.push(`period=${params.period}`);

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<any>(`/admin/suppliers/statistics${queryString}`);
  },

  // Get products for a specific supplier
  getSupplierProducts: (supplierId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{
    products: any[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.category) queryParams.push(`category=${params.category}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
    }

    // Add supplier filter to use the correct endpoint
    queryParams.push(`supplier=${supplierId}`);

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      products: any[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
      };
    }>(`/admin/supplier-products${queryString}`);
  },
};

// Pricing API
export const PricingAPI = {
  // Get all products with pricing
  getAllProductsWithPricing: (params?: {
    search?: string;
    category?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    showDiscontinued?: boolean;
  }): Promise<ApiResponse<PricingPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.category) queryParams.push(`category=${params.category}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.showDiscontinued !== undefined) queryParams.push(`showDiscontinued=${params.showDiscontinued}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<PricingPaginatedResponse>(`/pricing/products${queryString}`);
  },

  // Update product base price
  updateBasePrice: (productId: string, data: {
    basePrice: number;
    taxRate?: number;
    discount?: number;
    updateSellingPrice?: boolean;
    reason?: string;
  }): Promise<ApiResponse<{ product: ProductPricing }>> =>
    fetchAPI<{ product: ProductPricing }>(`/pricing/products/${productId}/base-price`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Update product selling price
  updateSellingPrice: (productId: string, data: {
    sellingPrice: number;
    reason?: string;
  }): Promise<ApiResponse<{ product: ProductPricing }>> =>
    fetchAPI<{ product: ProductPricing }>(`/pricing/products/${productId}/selling-price`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Get product price history
  getProductPriceHistory: (productId: string, params?: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PriceHistoryPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<PriceHistoryPaginatedResponse>(`/pricing/products/${productId}/history${queryString}`);
  },

  // Bulk price update
  bulkPriceUpdate: (data: {
    products: string[];
    priceChange: number;
    changeType: 'fixed' | 'percentage';
    updateSellingPrice?: boolean;
    reason?: string;
  }): Promise<ApiResponse<{
    success: Array<{
      productId: string;
      oldPrice: number;
      newPrice: number;
    }>;
    failed: any[];
  }>> =>
    fetchAPI<{
      success: Array<{
        productId: string;
        oldPrice: number;
        newPrice: number;
      }>;
      failed: any[];
    }>('/pricing/bulk-update', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Schedule a time-based discount
  addTimeBasedDiscount: (productId: string, data: {
    discountPercentage: number;
    effectiveFrom: string;
    effectiveTo: string;
    reason?: string;
    updateSellingPrice?: boolean;
  }): Promise<ApiResponse<TimeBasedDiscount>> =>
    fetchAPI<TimeBasedDiscount>(`/pricing/products/${productId}/discounts`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Get product discounts
  getProductDiscounts: (productId: string, includeExpired: boolean = false): Promise<ApiResponse<DiscountsResponse>> =>
    fetchAPI<DiscountsResponse>(`/pricing/products/${productId}/discounts?includeExpired=${includeExpired}`),

  // Update a discount
  updateDiscount: (productId: string, discountId: string, data: {
    discountPercentage?: number;
    effectiveFrom?: string;
    effectiveTo?: string;
    isActive?: boolean;
    reason?: string;
  }): Promise<ApiResponse<TimeBasedDiscount>> =>
    fetchAPI<TimeBasedDiscount>(`/pricing/products/${productId}/discounts/${discountId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Delete a discount
  deleteDiscount: (productId: string, discountId: string): Promise<ApiResponse<{ id: string }>> =>
    fetchAPI<{ id: string }>(`/pricing/products/${productId}/discounts/${discountId}`, {
      method: 'DELETE'
    })
};

// Inventory API
export const InventoryAPI = {
  // Get inventory dashboard
  getDashboard: (): Promise<ApiResponse<InventoryDashboard>> =>
    fetchAPI<InventoryDashboard>('/admin/inventory/dashboard'),

  // Get all products inventory
  getAllProducts: (params?: {
    search?: string;
    category?: string;
    supplier?: string;
    lowStock?: boolean;
    outOfStock?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<InventoryProductsResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.category) queryParams.push(`category=${params.category}`);
      if (params.supplier) queryParams.push(`supplier=${params.supplier}`);
      if (params.lowStock !== undefined) queryParams.push(`lowStock=${params.lowStock}`);
      if (params.outOfStock !== undefined) queryParams.push(`outOfStock=${params.outOfStock}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<InventoryProductsResponse>(`/admin/inventory/products${queryString}`);
  },

  // Get category inventory
  getCategoryInventory: (categoryId: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<InventoryCategoryResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<InventoryCategoryResponse>(`/admin/inventory/categories/${categoryId}${queryString}`);
  },

  // Get all suppliers inventory
  getAllSuppliers: (params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<InventorySuppliersResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<InventorySuppliersResponse>(`/admin/inventory/suppliers${queryString}`);
  },

  // Get supplier inventory detailed
  getSupplierInventory: (supplierId: string, params?: {
    search?: string;
    category?: string;
    lowStock?: boolean;
    outOfStock?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<InventorySupplierDetailedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.category) queryParams.push(`category=${params.category}`);
      if (params.lowStock !== undefined) queryParams.push(`lowStock=${params.lowStock}`);
      if (params.outOfStock !== undefined) queryParams.push(`outOfStock=${params.outOfStock}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<InventorySupplierDetailedResponse>(`/admin/inventory/suppliers/${supplierId}${queryString}`);
  }
};

// Transporters API
export const TransporterAPI = {
  // Admin Endpoints

  // Get all transporters
  getAllTransporters: (params?: {
    search?: string;
    isActive?: boolean;
    verificationStatus?: 'pending' | 'verified' | 'rejected';
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedTransporterResponse>> => {
    const queryParams = [];

    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
      if (params.verificationStatus) queryParams.push(`verificationStatus=${params.verificationStatus}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<PaginatedTransporterResponse>(`/transporters${queryString}`);
  },

  // Get a single transporter by ID
  getTransporter: (id: string): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>(`/transporters/${id}`),

  // Create a new transporter
  createTransporter: (transporterData: TransporterCreateRequest): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>('/transporters', {
      method: 'POST',
      body: JSON.stringify(transporterData)
    }),

  // Register a new transporter with credentials
  registerTransporter: (transporterData: TransporterCreateRequest): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>('/transporters/register', {
      method: 'POST',
      body: JSON.stringify(transporterData)
    }),

  // Update a transporter
  updateTransporter: (id: string, transporterData: TransporterUpdateRequest): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>(`/transporters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transporterData)
    }),

  // Delete a transporter
  deleteTransporter: (id: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/transporters/${id}`, {
      method: 'DELETE'
    }),

  // Verify a transporter
  verifyTransporter: (id: string): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>(`/transporters/${id}/verify`, {
      method: 'PUT'
    }),

  // Reject a transporter
  rejectTransporter: (id: string, reason: string): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>(`/transporters/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason })
    }),

  // Add vehicle to transporter
  addVehicle: (transporterId: string, vehicleData: TransporterVehicleRequest): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>(`/transporters/${transporterId}/vehicles`, {
      method: 'POST',
      body: JSON.stringify(vehicleData)
    }),

  // Delete vehicle from transporter
  deleteVehicle: (transporterId: string, vehicleId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/transporters/${transporterId}/vehicles/${vehicleId}`, {
      method: 'DELETE'
    }),

  // Transporter Portal Endpoints

  // Login
  login: (credentials: { email: string; password: string }): Promise<ApiResponse<{ transporter: Transporter; token: string }>> =>
    fetchAPI<{ transporter: Transporter; token: string }>('/transporter-portal/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    }),

  // Forgot Password
  forgotPassword: (email: string): Promise<ApiResponse<{ resetToken: string; message: string }>> =>
    fetchAPI<{ resetToken: string; message: string }>('/transporter-portal/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    }),

  // Reset Password
  resetPassword: (resetToken: string, password: string): Promise<ApiResponse<{ token: string }>> =>
    fetchAPI<{ token: string }>(`/transporter-portal/reset-password/${resetToken}`, {
      method: 'PUT',
      body: JSON.stringify({ password })
    }),

  // Get Current Transporter Profile
  getProfile: (): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>('/transporter-portal/me'),

  // Update Profile
  updateProfile: (profileData: Partial<TransporterUpdateRequest>): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>('/transporter-portal/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    }),

  // Change Password
  changePassword: (passwords: { currentPassword: string; newPassword: string }): Promise<ApiResponse<any>> =>
    fetchAPI<any>('/transporter-portal/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwords)
    }),

  // Driver Management Endpoints

  // Add Driver
  addDriver: (driverData: TransporterDriverRequest): Promise<ApiResponse<Transporter>> =>
    fetchAPI<Transporter>('/transporter-portal/drivers', {
      method: 'POST',
      body: JSON.stringify(driverData)
    }),

  // Get All Drivers
  getAllDrivers: (): Promise<ApiResponse<any>> =>
    fetchAPI<any>('/transporter-portal/drivers'),

  // Get Driver by ID
  getDriver: (driverId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/transporter-portal/drivers/${driverId}`),

  // Update Driver
  updateDriver: (driverId: string, driverData: Partial<TransporterDriverRequest>): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/transporter-portal/drivers/${driverId}`, {
      method: 'PUT',
      body: JSON.stringify(driverData)
    }),

  // Delete Driver
  deleteDriver: (driverId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/transporter-portal/drivers/${driverId}`, {
      method: 'DELETE'
    }),

  // Add Document to Driver
  addDriverDocument: (driverId: string, document: File, name: string, type: string): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('document', document);
    formData.append('name', name);
    formData.append('type', type);

    return fetchFormAPI<any>(`/transporter-portal/drivers/${driverId}/documents`, formData);
  },

  // Delete Document from Driver
  deleteDriverDocument: (driverId: string, documentId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/transporter-portal/drivers/${driverId}/documents/${documentId}`, {
      method: 'DELETE'
    })
};

// Admin Transporter Management API (New endpoints with /api/admin prefix)
export const AdminTransporterAPI = {
  // Get all transporters with comprehensive statistics
  getAllTransporters: (params?: {
    search?: string;
    verificationStatus?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
    includeStats?: boolean;
  }): Promise<ApiResponse<TransporterPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.verificationStatus) queryParams.push(`verificationStatus=${params.verificationStatus}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.includeStats) queryParams.push(`includeStats=${params.includeStats}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<TransporterPaginatedResponse>(`/admin/transporters${queryString}`);
  },

  // Get comprehensive transporter details with analytics
  getTransporterById: (id: string): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${id}`),

  // Get transporter fleet management details
  getTransporterFleet: (id: string): Promise<ApiResponse<TransporterFleet>> =>
    fetchAPI<TransporterFleet>(`/admin/transporters/${id}/fleet`),

  // Get transporter route history
  getTransporterRoutes: (id: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<TransporterRoutePaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.status) queryParams.push(`status=${params.status}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<TransporterRoutePaginatedResponse>(`/admin/transporters/${id}/routes${queryString}`);
  },

  // Get transporter analytics
  getTransporterAnalytics: (id: string, params?: {
    period?: '7d' | '30d' | '90d' | '1y';
  }): Promise<ApiResponse<TransporterAnalytics>> => {
    const queryParams = [];
    if (params?.period) queryParams.push(`period=${params.period}`);

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<TransporterAnalytics>(`/admin/transporters/${id}/analytics${queryString}`);
  },

  // Update transporter details and verification status
  updateTransporter: (id: string, transporterData: {
    name?: string;
    email?: string;
    mobile?: string;
    alternatePhone?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      pincode?: string;
      country?: string;
    };
    verificationStatus?: 'pending' | 'verified' | 'rejected';
    isActive?: boolean;
    adminNotes?: string;
    rejectionReason?: string;
    [key: string]: any;
  }): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transporterData)
    }),

  // Delete transporter (soft delete or permanent delete)
  deleteTransporter: (id: string, options?: {
    permanent?: boolean;
    reason?: string;
  }): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/transporters/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(options || {})
    }),

  // Get comprehensive transporter statistics
  getTransporterStatistics: (params?: {
    period?: '7d' | '30d' | '90d' | '1y';
  }): Promise<ApiResponse<TransporterStats>> => {
    const queryParams = [];
    if (params?.period) queryParams.push(`period=${params.period}`);

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<TransporterStats>(`/admin/transporters/statistics${queryString}`);
  },

  // Vehicle management endpoints
  addVehicle: (transporterId: string, vehicleData: {
    registrationNumber: string;
    type: string;
    capacity: {
      value: number;
      unit: string;
    };
    insuranceExpiryDate: string;
    documents?: Array<{
      name: string;
      type: string;
      fileUrl: string;
      expiryDate?: string;
    }>;
  }): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${transporterId}/vehicles`, {
      method: 'POST',
      body: JSON.stringify(vehicleData)
    }),

  updateVehicle: (transporterId: string, vehicleId: string, vehicleData: {
    registrationNumber?: string;
    type?: string;
    capacity?: {
      value: number;
      unit: string;
    };
    insuranceExpiryDate?: string;
    isActive?: boolean;
    status?: 'available' | 'in_transit' | 'maintenance' | 'inactive';
  }): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${transporterId}/vehicles/${vehicleId}`, {
      method: 'PUT',
      body: JSON.stringify(vehicleData)
    }),

  deleteVehicle: (transporterId: string, vehicleId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/transporters/${transporterId}/vehicles/${vehicleId}`, {
      method: 'DELETE'
    }),

  // Driver management endpoints
  addDriver: (transporterId: string, driverData: {
    name: string;
    mobile: string;
    licenseNumber: string;
    licenseExpiry: string;
    documents?: Array<{
      name: string;
      type: string;
      fileUrl: string;
      expiryDate?: string;
    }>;
  }): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${transporterId}/drivers`, {
      method: 'POST',
      body: JSON.stringify(driverData)
    }),

  updateDriver: (transporterId: string, driverId: string, driverData: {
    name?: string;
    mobile?: string;
    licenseNumber?: string;
    licenseExpiry?: string;
    isActive?: boolean;
    currentStatus?: 'available' | 'on_trip' | 'break' | 'inactive';
  }): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${transporterId}/drivers/${driverId}`, {
      method: 'PUT',
      body: JSON.stringify(driverData)
    }),

  deleteDriver: (transporterId: string, driverId: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/transporters/${transporterId}/drivers/${driverId}`, {
      method: 'DELETE'
    }),

  // Location tracking endpoints
  updateLocation: (transporterId: string, locationData: {
    latitude: number;
    longitude: number;
    address?: string;
  }): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${transporterId}/location`, {
      method: 'PUT',
      body: JSON.stringify(locationData)
    }),

  getLocationHistory: (transporterId: string, params?: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<{
    latitude: number;
    longitude: number;
    address: string;
    timestamp: string;
  }>>> => {
    const queryParams = [];
    if (params) {
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<PaginatedResponse<{
      latitude: number;
      longitude: number;
      address: string;
      timestamp: string;
    }>>(`/admin/transporters/${transporterId}/location/history${queryString}`);
  },

  // Create a new transporter (Admin)
  createTransporter: (transporterData: TransporterCreateRequest): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>('/admin/transporters', {
      method: 'POST',
      body: JSON.stringify(transporterData)
    }),

  // Get transporter order history
  getTransporterOrderHistory: (transporterId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<OrderPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.status) queryParams.push(`status=${params.status}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<OrderPaginatedResponse>(`/admin/transporters/${transporterId}/orders${queryString}`);
  },

  // Document management endpoints
  uploadTransporterDocument: (transporterId: string, formData: FormData): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${transporterId}/documents`, {
      method: 'POST',
      body: formData,
      headers: {} // Let the browser set the content type for FormData
    }),

  deleteTransporterDocument: (transporterId: string, documentId: string): Promise<ApiResponse<EnhancedTransporter>> =>
    fetchAPI<EnhancedTransporter>(`/admin/transporters/${transporterId}/documents/${documentId}`, {
      method: 'DELETE'
    }),

  // Alias methods for backward compatibility
  addTransporterVehicle: (transporterId: string, vehicleData: any): Promise<ApiResponse<EnhancedTransporter>> =>
    AdminTransporterAPI.addVehicle(transporterId, vehicleData),

  updateTransporterVehicle: (transporterId: string, vehicleId: string, vehicleData: any): Promise<ApiResponse<EnhancedTransporter>> =>
    AdminTransporterAPI.updateVehicle(transporterId, vehicleId, vehicleData),

  deleteTransporterVehicle: (transporterId: string, vehicleId: string): Promise<ApiResponse<any>> =>
    AdminTransporterAPI.deleteVehicle(transporterId, vehicleId),
};

// Mobile User Management API
export const AdminMobileUserAPI = {
  // Get all mobile users with comprehensive statistics
  getAllMobileUsers: (params?: {
    search?: string;
    isActive?: boolean;
    isPhoneVerified?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
    includeStats?: boolean;
  }): Promise<ApiResponse<MobileUserPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
      if (params.isPhoneVerified !== undefined) queryParams.push(`isPhoneVerified=${params.isPhoneVerified}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.includeStats) queryParams.push(`includeStats=${params.includeStats}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<MobileUserPaginatedResponse>(`/admin/mobile-users${queryString}`);
  },

  // Get mobile user by ID with comprehensive details
  getMobileUserById: (userId: string): Promise<ApiResponse<EnhancedMobileUser>> =>
    fetchAPI<EnhancedMobileUser>(`/admin/mobile-users/${userId}`),

  // Get user order history
  getUserOrderHistory: (userId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<UserOrderHistoryResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.status) queryParams.push(`status=${params.status}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<UserOrderHistoryResponse>(`/admin/mobile-users/${userId}/orders${queryString}`);
  },

  // Get user cart
  getUserCart: (userId: string): Promise<ApiResponse<UserCart>> =>
    fetchAPI<UserCart>(`/admin/mobile-users/${userId}/cart`),

  // Get user's MASKED tax identity (PAN/GSTIN) + verification/consent status.
  getUserTaxInfo: (userId: string): Promise<ApiResponse<{
    hasData: boolean;
    pan?: { masked: string | null; verified: boolean; holderName: string | null; entityType: string | null } | null;
    gstin?: { masked: string | null; verified: boolean; businessName: string | null; stateName: string | null } | null;
    consent?: { given: boolean; at: string | null; dataProcessing: boolean; thirdPartySharing: boolean };
    status?: string;
    updatedAt?: string;
  }>> =>
    fetchAPI(`/admin/mobile-users/${userId}/tax-info`),

  // Update mobile user
  updateMobileUser: (userId: string, userData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    isActive?: boolean;
    isPhoneVerified?: boolean;
    adminNotes?: string;
  }): Promise<ApiResponse<EnhancedMobileUser>> =>
    fetchAPI<EnhancedMobileUser>(`/admin/mobile-users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    }),

  // Delete mobile user
  deleteMobileUser: (userId: string, options?: {
    permanent?: boolean;
    reason?: string;
  }): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/mobile-users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify(options || {})
    }),

  // Send notification to user
  sendNotificationToUser: (userId: string, notificationData: {
    title: string;
    body: string;
    type: string;
    category: string;
    actionData?: any;
  }): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/mobile-users/${userId}/notifications`, {
      method: 'POST',
      body: JSON.stringify(notificationData)
    }),

  // Get mobile user statistics
  getMobileUserStatistics: (params?: {
    period?: '7d' | '30d' | '90d' | '1y';
  }): Promise<ApiResponse<MobileUserStats>> => {
    const queryParams = [];
    if (params?.period) queryParams.push(`period=${params.period}`);

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<MobileUserStats>(`/admin/mobile-users/statistics${queryString}`);
  },
};

// Mobile User interfaces for Admin API
export interface MobileUser {
  _id: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phone: string;
  isActive: boolean;
  isPhoneVerified: boolean;
  registrationDate: string;
  lastLogin?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  cartItemsCount: number;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate?: string;
  adminNotes?: string;
  fcmToken?: string;
  createdAt: string;
  updatedAt: string;
}

// Enhanced Mobile User interface with comprehensive details
export interface EnhancedMobileUser extends MobileUser {
  phoneNumber: string;
  profilePicture?: string;
  deviceInfo?: string;
  loginAttempts?: number;
  orderCount?: number;

  // Full notification preferences at root level
  notificationPreferences?: {
    orderUpdates: boolean;
    promotions: boolean;
    deliveryUpdates: boolean;
    paymentUpdates: boolean;
    appUpdates: boolean;
  };

  // Privacy settings
  privacySettings?: {
    shareLocationData: boolean;
    shareUsageData: boolean;
    allowPersonalizedAds: boolean;
  };

  addresses: Array<{
    _id: string;
    name: string;
    phoneNumber: string;
    type: 'home' | 'work' | 'other';
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    nearbyLandmark?: string;
    isDefault: boolean;
    createdAt?: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  }>;

  taxInfo?: {
    hasPAN: boolean;
    hasGSTIN: boolean;
    panMasked?: string;
    verificationStatus: 'pending' | 'verified' | 'rejected';
  };

  // Extended order stats from API
  orderStats?: {
    _id: string | null;
    totalOrders: number;
    completedOrders: number;
    pendingOrders: number;
    processingOrders: number;
    cancelledOrders: number;
    totalSpent: number;
    avgOrderValue: number;
    lastOrderDate?: string;
    firstOrderDate?: string;
  };

  // Keep backward compatibility - orderSummary
  orderSummary: {
    totalOrders: number;
    totalSpent: number;
    averageOrderValue: number;
    pendingOrders: number;
    completedOrders: number;
    cancelledOrders: number;
    lastOrderDate?: string;
  };

  // Full cart details from API
  cartDetails?: {
    discount?: {
      amount: number;
    };
    deliveryLocation?: {
      coordinates?: {
        latitude: number;
        longitude: number;
      };
      name?: string;
      phoneNumber?: string;
      street?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
      nearbyLandmark?: string;
      isDefault?: boolean;
      deliveryInstructions?: string;
    };
    timeSlot?: {
      isAvailable: boolean;
    };
    _id?: string;
    userId?: string;
    items?: Array<{
      productId?: {
        _id: string;
        name: string;
        images?: Array<{
          url: string;
          alt?: string;
          isDefault?: boolean;
          public_id?: string;
          _id?: string;
        }>;
        averagePrice?: number;
        isCompliant?: boolean;
      };
      name: string;
      description?: string;
      image?: string;
      price: number;
      discountedPrice?: number;
      quantity: number;
      unit?: string;
      minOrderQuantity?: number;
      maxOrderQuantity?: number;
      category?: string;
      isAvailable?: boolean;
      calculatedPrice?: number;
      addedAt?: string;
      updatedAt?: string;
      _id?: string;
    }>;
    totalItems?: number;
    subtotal?: number;
    deliveryCharges?: number;
    taxes?: number;
    totalAmount?: number;
    lastUpdated?: string;
    lastSyncAt?: string;
    expiresAt?: string;
    createdAt?: string;
    updatedAt?: string;
  };

  // Keep simplified summary for backward compatibility
  cartSummary: {
    itemCount: number;
    totalValue: number;
    lastUpdated: string;
  };

  // Recent orders from API response
  recentOrders?: Array<{
    _id: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    createdAt: string;
    deliveryAddress?: {
      coordinates?: {
        latitude: number;
        longitude: number;
      };
      name?: string;
      street?: string;
      city?: string;
      state?: string;
      pincode?: string;
    };
    items?: Array<{
      product?: {
        _id: string;
        name: string;
        images?: Array<{
          url: string;
          alt?: string;
          isDefault?: boolean;
          public_id?: string;
          _id?: string;
        }>;
        averagePrice?: number;
        isCompliant?: boolean;
      };
    }>;
    assignedSupplier?: {
      _id: string;
      businessName: string;
      ownerName: string;
    };
    transporter?: {
      _id: string;
      name: string;
      mobile: string;
    };
  }>;

  // Notification stats
  notificationStats?: {
    _id: string | null;
    totalNotifications: number;
    unreadNotifications: number;
    notificationsByType?: string[];
    typeBreakdown?: Record<string, number>;
  };

  // Payment methods
  paymentMethods?: Array<any>;

  // Discount usage
  usedDiscounts?: Array<any>;
  discountUsage?: Array<any>;

  // Extended engagement metrics
  engagementMetrics: {
    totalLogins: number;
    lastLoginAt?: string;
    averageSessionDuration: number;
    orderCompletionRate?: string;
    orderCancellationRate?: string;
    avgOrderValue?: number;
    lifetimeValue?: number;
    customerSince?: string;
    daysSinceLastOrder?: number;
    addressCount?: number;
    paymentMethodCount?: number;
    notificationEngagement?: string;
    totalDiscountsSaved?: number;
    notificationPreferences?: {
      orderUpdates: boolean;
      promotions: boolean;
      appUpdates: boolean;
      deliveryUpdates?: boolean;
      paymentUpdates?: boolean;
    };
  };
}

// Mobile User paginated response
export interface MobileUserPaginatedResponse {
  users: EnhancedMobileUser[];
  pagination: {
    totalUsers: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: {
    totalActiveUsers: number;
    totalVerifiedUsers: number;
    newUsersThisMonth: number;
    totalRevenue: number;
  };
}

// User order history response
export interface UserOrderHistoryResponse {
  orders: Array<{
    _id: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    items: Array<{
      productName: string;
      quantity: number;
      unit: string;
      pricePerUnit: number;
    }>;
    deliveryAddress: {
      name: string;
      phoneNumber: string;
      street: string;
      city: string;
      state: string;
      pincode: string;
    };
    supplierName?: string;
    expectedDeliveryDate?: string;
    actualDeliveryDate?: string;
    paymentStatus: string;
    createdAt: string;
  }>;
  pagination: {
    totalOrders: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: {
    totalValue: number;
    averageOrderValue: number;
    ordersByStatus: Record<string, number>;
  };
}

// User cart interface
export interface UserCart {
  _id: string;
  user: string;
  items: Array<{
    _id: string;
    product: {
      _id: string;
      name: string;
      price: number;
      images: string[];
    };
    quantity: number;
    unit: string;
    pricePerUnit: number;
    totalPrice: number;
    addedAt: string;
  }>;
  deliveryPreferences?: {
    preferredDate?: string;
    timeSlot?: string;
    deliveryAddress?: {
      _id: string;
      name: string;
      street: string;
      city: string;
    };
  };
  summary: {
    itemCount: number;
    subtotal: number;
    tax: number;
    totalAmount: number;
  };
  lastUpdated: string;
}

export interface MobileUserStats {
  overview: {
    totalUsers: number;
    activeUsers: number;
    verifiedUsers: number;
    newUsersThisPeriod: number;
    growthRate: number;
  };
  engagement: {
    dailyActiveUsers: number;
    weeklyActiveUsers: number;
    monthlyActiveUsers: number;
    averageSessionDuration: number;
    averageOrdersPerUser: number;
  };
  orders: {
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    ordersThisPeriod: number;
    revenueThisPeriod: number;
  };
  demographics: {
    topCities: Array<{
      city: string;
      userCount: number;
    }>;
    topStates: Array<{
      state: string;
      userCount: number;
    }>;
  };
  trends: {
    userGrowthChart: Array<{
      date: string;
      newUsers: number;
      totalUsers: number;
    }>;
    orderTrends: Array<{
      date: string;
      orders: number;
      revenue: number;
    }>;
  };
  generatedAt: string;
  period: string;
}



export interface MobileUserUpdateRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  isActive?: boolean;
  isPhoneVerified?: boolean;
  adminNotes?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
}



// Order Management API
export interface OperationalAlertOrder {
  _id: string;
  orderNumber: string;
  status: string;
  totalAmount: number;
  customerName: string;
  expectedDeliveryDate?: string;
  createdAt: string;
  overdueHours?: number;
  dueInHours?: number;
  waitingHours?: number;
}

export const OrderAPI = {
  // Get all orders with filtering and pagination
  getAllOrders: (params?: {
    page?: number;
    limit?: number;
    status?: string | string[];
    startDate?: string;
    endDate?: string;
    search?: string;
    supplier?: string;
    transporter?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    orders: Order[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    }
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.status) {
        if (Array.isArray(params.status)) {
          params.status.forEach(status => queryParams.push(`status=${status}`));
        } else {
          queryParams.push(`status=${params.status}`);
        }
      }
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.supplier) queryParams.push(`supplier=${params.supplier}`);
      if (params.transporter) queryParams.push(`transporter=${params.transporter}`);
      if (params.sort) queryParams.push(`sort=${params.sort}`);
      if (params.order) queryParams.push(`order=${params.order}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      orders: Order[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      }
    }>(`/users/orders${queryString}`);
  },

  // Get a single order by ID
  getOrder: (id: string): Promise<ApiResponse<Order>> =>
    fetchAPI<Order>(`/users/orders/${id}`),

  // Assign order to supplier
  assignSupplier: (
    orderId: string,
    data: {
      supplierId: string;
      itemIds?: string[];
      notes?: string;
    }
  ): Promise<ApiResponse<{
    orderId: string;
    orderNumber: string;
    status: string;
    supplier: {
      id: string;
      name: string;
    }
  }>> => {
    console.log(`Calling assign supplier API for order ${orderId} with data:`, JSON.stringify(data));
    return fetchAPI<{
      orderId: string;
      orderNumber: string;
      status: string;
      supplier: {
        id: string;
        name: string;
      }
    }>(`/users/orders/${orderId}/assign-supplier`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Assign order to transporter
  assignTransporter: (
    orderId: string,
    data: {
      transporterId: string;
      vehicleDetails?: {
        registrationNumber?: string;
        type?: string;
        capacity?: {
          value: number;
          unit: string;
        }
      };
      notes?: string;
    }
  ): Promise<ApiResponse<{
    orderId: string;
    orderNumber: string;
    status: string;
    transporter: {
      id: string;
      name: string;
    }
  }>> =>
    fetchAPI<{
      orderId: string;
      orderNumber: string;
      status: string;
      transporter: {
        id: string;
        name: string;
      }
    }>(`/users/orders/${orderId}/assign-transporter`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Update order status
  updateStatus: (
    orderId: string,
    data: {
      status: OrderStatus;
      notes?: string;
    }
  ): Promise<ApiResponse<{
    orderId: string;
    orderNumber: string;
    status: string;
    statusHistory: Array<{
      status: string;
      timestamp: string;
      updatedBy?: string;
      userType?: string;
      notes?: string;
    }>
  }>> =>
    fetchAPI<{
      orderId: string;
      orderNumber: string;
      status: string;
      statusHistory: Array<{
        status: string;
        timestamp: string;
        updatedBy?: string;
        userType?: string;
        notes?: string;
      }>
    }>(`/users/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Get order statistics
  getStatistics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{
    totalOrders: number;
    totalAmount: number;
    avgOrderValue: number;
    statusCounts: Record<string, number>;
    ordersPerDay: Array<{
      date: string;
      count: number;
      total: number;
    }>;
    topSuppliers: Array<{
      id: string;
      name: string;
      count: number;
    }>;
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.period) queryParams.push(`period=${params.period}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      totalOrders: number;
      totalAmount: number;
      avgOrderValue: number;
      statusCounts: Record<string, number>;
      ordersPerDay: Array<{
        date: string;
        count: number;
        total: number;
      }>;
      topSuppliers: Array<{
        id: string;
        name: string;
        count: number;
      }>;
    }>(`/users/orders/statistics${queryString}`);
  },

  // Operational alerts for the dashboard — orders needing attention.
  getOperationalAlerts: (): Promise<ApiResponse<{
    generatedAt: string;
    alerts: {
      late: { count: number; orders: OperationalAlertOrder[] };
      dueSoon: { count: number; orders: OperationalAlertOrder[] };
      awaitingAcceptance: { count: number; orders: OperationalAlertOrder[] };
      awaitingTransporter: { count: number; orders: OperationalAlertOrder[] };
    };
  }>> => fetchAPI(`/users/orders/operational-alerts`),

  // Live tracking for an order: driver position, suggested route, pickup/drop.
  getLiveTracking: (orderId: string): Promise<ApiResponse<{
    orderId: string;
    orderNumber: string;
    status: string;
    isLive: boolean;
    driver: { name: string; phone: string } | null;
    driverLocation: { latitude: number; longitude: number; heading?: number; speed?: number; updatedAt?: string } | null;
    pickup: { name: string; address: string; latitude: number; longitude: number } | null;
    delivery: { name: string; address: string; latitude: number | null; longitude: number | null };
    route: { coordinates: [number, number][]; distance: number | null; duration: number | null };
    expectedDeliveryDate: string | null;
  }>> => fetchAPI(`/users/orders/${orderId}/track`),

  // Orders with a pending customer cancellation request
  getCancellationRequests: (params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Order[]>> => {
    const qp: string[] = [];
    if (params?.page) qp.push(`page=${params.page}`);
    if (params?.limit) qp.push(`limit=${params.limit}`);
    const qs = qp.length ? `?${qp.join('&')}` : '';
    return fetchAPI<Order[]>(`/users/orders/cancellation-requests${qs}`);
  },

  // Approve or reject a cancellation request; on approve the admin also chooses
  // the refund ('none' | 'full' | 'partial').
  decideCancellation: (
    orderId: string,
    data: {
      decision: 'approve' | 'reject';
      refundDecision?: 'none' | 'full' | 'partial';
      refundAmount?: number;
      note?: string;
    }
  ): Promise<ApiResponse<{
    orderId: string;
    orderNumber?: string;
    status?: string;
    refundDecision?: string;
    refundInitiated?: boolean;
    refundId?: string | null;
  }>> =>
    fetchAPI<{
      orderId: string;
      orderNumber?: string;
      status?: string;
      refundDecision?: string;
      refundInitiated?: boolean;
      refundId?: string | null;
    }>(`/users/orders/${orderId}/cancellation-decision`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
};

// Product Approval API
export const ProductApprovalAPI = {
  // Get all pending product submissions
  getPendingProducts: (params?: {
    page?: number;
    limit?: number;
    category?: string;
  }): Promise<ApiResponse<{
    products: any[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    }
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.category) queryParams.push(`category=${params.category}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      products: any[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
      }
    }>(`/products/pending${queryString}`);
  },

  // Get product approval details
  getProductApprovalDetails: (id: string): Promise<ApiResponse<{
    product: any;
    pricingComparison: any | null;
  }>> =>
    fetchAPI<{
      product: any;
      pricingComparison: any | null;
    }>(`/products/${id}/approval-details`),

  // Approve or reject product submission
  approveOrRejectProduct: (
    id: string,
    data: {
      action: 'approve' | 'reject';
      basePrice?: number;
      sellingPrice?: number;
      taxRate?: number;
      discount?: number;
      rejectionReason?: string;
    }
  ): Promise<ApiResponse<{
    _id: string;
    name: string;
    status: 'approved' | 'rejected';
    rejectionReason?: string;
    basePrice?: number;
    sellingPrice?: number;
    taxRate?: number;
    discount?: number;
    approvedBy?: string;
    approvedAt?: string;
    createdAt: string;
    updatedAt: string;
  }>> =>
    fetchAPI<{
      _id: string;
      name: string;
      status: 'approved' | 'rejected';
      rejectionReason?: string;
      basePrice?: number;
      sellingPrice?: number;
      taxRate?: number;
      discount?: number;
      approvedBy?: string;
      approvedAt?: string;
      createdAt: string;
      updatedAt: string;
    }>(`/products/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
};

// Supplier Products Management API
export const SupplierProductsAPI = {
  // Get all supplier products with comprehensive filtering
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    supplier?: string;
    category?: string;
    isActive?: boolean;
    lowStock?: boolean;
    outOfStock?: boolean;
    sortBy?: 'createdAt' | 'price' | 'name';
    sortOrder?: 'asc' | 'desc';
    minPrice?: number;
    maxPrice?: number;
  }): Promise<ApiResponse<{
    products: any[];
    pagination: {
      total: number;
      totalInDB: number;
      page: number;
      limit: number;
      pages: number;
    };
    summary: {
      totalProducts: number;
      activeProducts: number;
      lowStockProducts: number;
      outOfStockProducts: number;
    };
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.supplier) queryParams.push(`supplier=${params.supplier}`);
      if (params.category) queryParams.push(`category=${params.category}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
      if (params.lowStock) queryParams.push(`lowStock=${params.lowStock}`);
      if (params.outOfStock) queryParams.push(`outOfStock=${params.outOfStock}`);
      if (params.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
      if (params.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);
      if (params.minPrice) queryParams.push(`minPrice=${params.minPrice}`);
      if (params.maxPrice) queryParams.push(`maxPrice=${params.maxPrice}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      products: any[];
      pagination: {
        total: number;
        totalInDB: number;
        page: number;
        limit: number;
        pages: number;
      };
      summary: {
        totalProducts: number;
        activeProducts: number;
        lowStockProducts: number;
        outOfStockProducts: number;
      };
    }>(`/admin/supplier-products${queryString}`);
  },

  // Get supplier products statistics
  getStatistics: (params?: {
    supplierId?: string;
    period?: 'day' | 'week' | 'month' | 'year';
  }): Promise<ApiResponse<any>> => {
    const queryParams = [];
    if (params) {
      if (params.supplierId) queryParams.push(`supplierId=${params.supplierId}`);
      if (params.period) queryParams.push(`period=${params.period}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<any>(`/admin/supplier-products/stats${queryString}`);
  },

  // Create new product for supplier
  create: (data: {
    supplierId: string;
    name: string;
    description: string;
    categoryId: string;
    unit: string;
    basePrice?: number;
    price: number;
    maxQuantity?: number;
    minOrderQuantity?: number;
    initialStock?: number;
    lowStockThreshold?: number;
    reorderPoint?: number;
    specifications?: Array<{
      name: string;
      value: string;
    }>;
    images?: string[];
    hsnCode?: string;
    gstRate?: number;
    adminNotes?: string;
  }): Promise<ApiResponse<{
    supplierProduct: any;
    productId: string;
    initialStock: number;
    inventoryCreated: boolean;
    imagesUploaded: number;
  }>> =>
    fetchAPI<{
      supplierProduct: any;
      productId: string;
      initialStock: number;
      inventoryCreated: boolean;
      imagesUploaded: number;
    }>(`/admin/supplier-products`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Create new product with file upload
  createWithFiles: (formData: FormData): Promise<ApiResponse<{
    supplierProduct: any;
    productId: string;
    initialStock: number;
    inventoryCreated: boolean;
    imagesUploaded: number;
  }>> =>
    fetchFormAPI<{
      supplierProduct: any;
      productId: string;
      initialStock: number;
      inventoryCreated: boolean;
      imagesUploaded: number;
    }>(`/admin/supplier-products`, formData),

  // Update supplier product stock
  updateStock: (
    id: string,
    data: {
      quantity: number;
      transactionType: 'add' | 'remove' | 'adjust';
      notes?: string;
    }
  ): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/supplier-products/${id}/stock`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),



  // Enhanced update supplier product
  fullUpdate: (
    id: string,
    data: {
      price?: number;
      maxQuantity?: number;
      minOrderQuantity?: number;
      isActive?: boolean;
      adminNotes?: string;
      productDetails?: {
        name?: string;
        description?: string;
        unit?: string;
        basePrice?: number;
        specifications?: Array<{
          name: string;
          value: string;
        }>;
        hsnCode?: string;
        gstRate?: number;
      };
    }
  ): Promise<ApiResponse<{
    supplierProduct: any;
    changes: string[];
    updatedFields: {
      supplierProductFields: boolean;
      productFields: boolean;
    };
  }>> =>
    fetchAPI<{
      supplierProduct: any;
      changes: string[];
      updatedFields: {
        supplierProductFields: boolean;
        productFields: boolean;
      };
    }>(`/admin/supplier-products/${id}/full-update`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Update supplier product images
  updateImages: (id: string, formData: FormData): Promise<ApiResponse<{
    productId: string;
    supplierProduct: any;
    changes: string[];
    totalImages: number;
  }>> =>
    fetchFormAPI<{
      productId: string;
      supplierProduct: any;
      changes: string[];
      totalImages: number;
    }>(`/admin/supplier-products/${id}/images`, formData, 'PUT'),

  // Get available categories for product creation
  getAvailableCategories: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{
    categories: any[];
    pagination: {
      total: number;
      totalInDB: number;
      page: number;
      limit: number;
      pages: number;
    };
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      categories: any[];
      pagination: {
        total: number;
        totalInDB: number;
        page: number;
        limit: number;
        pages: number;
      };
    }>(`/admin/supplier-products/available-categories${queryString}`);
  },

  // Get available suppliers for product creation
  getAvailableSuppliers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    verificationStatus?: 'pending' | 'verified' | 'rejected';
  }): Promise<ApiResponse<{
    suppliers: any[];
    pagination: {
      total: number;
      totalInDB: number;
      page: number;
      limit: number;
      pages: number;
    };
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.verificationStatus) queryParams.push(`verificationStatus=${params.verificationStatus}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      suppliers: any[];
      pagination: {
        total: number;
        totalInDB: number;
        page: number;
        limit: number;
        pages: number;
      };
    }>(`/admin/supplier-products/available-suppliers${queryString}`);
  },

  // Delete supplier product
  delete: (id: string): Promise<ApiResponse<{
    deletedId: string;
    message: string;
  }>> =>
    fetchAPI<{
      deletedId: string;
      message: string;
    }>(`/admin/supplier-products/${id}`, {
      method: 'DELETE'
    }),

  // Get supplier product by ID
  getById: (id: string): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/supplier-products/${id}`),

  // Update inventory thresholds
  updateThresholds: (
    id: string,
    data: {
      lowStockThreshold: number;
      reorderPoint: number;
    }
  ): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/supplier-products/${id}/thresholds`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Get transactions history
  getTransactions: (
    id: string,
    params?: {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
      transactionType?: 'add' | 'remove' | 'adjust';
    }
  ): Promise<ApiResponse<any>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.transactionType) queryParams.push(`transactionType=${params.transactionType}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<any>(`/admin/supplier-products/${id}/transactions${queryString}`);
  },

  // Get price history
  getPriceHistory: (
    id: string,
    params?: {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<ApiResponse<any>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<any>(`/admin/supplier-products/${id}/price-history${queryString}`);
  },

  // Toggle supplier product active status
  toggleActive: (
    id: string,
    data?: {
      reason?: string;
    }
  ): Promise<ApiResponse<{
    _id: string;
    isActive: boolean;
    updatedAt: string;
    reason?: string;
  }>> =>
    fetchAPI<{
      _id: string;
      isActive: boolean;
      updatedAt: string;
      reason?: string;
    }>(`/admin/supplier-products/${id}/toggle-active`, {
      method: 'PUT',
      body: JSON.stringify(data || {})
    })
};

// Discount Management API
export const DiscountAPI = {
  // Get all discounts with pagination and filtering
  getAll: (params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    status?: 'active' | 'inactive' | 'expired' | 'upcoming';
    search?: string;
  }): Promise<ApiResponse<DiscountPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.sort) queryParams.push(`sort=${params.sort}`);
      if (params.order) queryParams.push(`order=${params.order}`);
      if (params.status) queryParams.push(`status=${params.status}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<DiscountPaginatedResponse>(`/users/discounts${queryString}`);
  },

  // Get discount by ID
  getById: (id: string): Promise<ApiResponse<{ discount: Discount }>> =>
    fetchAPI<{ discount: Discount }>(`/users/discounts/${id}`),

  // Create new discount
  create: (data: DiscountCreateRequest): Promise<ApiResponse<{ discount: Discount }>> =>
    fetchAPI<{ discount: Discount }>('/users/discounts', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Update discount
  update: (id: string, data: DiscountUpdateRequest): Promise<ApiResponse<{ discount: Discount }>> =>
    fetchAPI<{ discount: Discount }>(`/users/discounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // Delete discount
  delete: (id: string): Promise<ApiResponse<{ id: string }>> =>
    fetchAPI<{ id: string }>(`/users/discounts/${id}`, {
      method: 'DELETE'
    })
};

// Notification Management API
export const NotificationAPI = {
  // Get notification dashboard
  getDashboard: (params?: {
    period?: '24h' | '7d' | '30d' | '90d';
  }): Promise<ApiResponse<NotificationDashboard>> => {
    const queryParams = [];
    if (params) {
      if (params.period) queryParams.push(`period=${params.period}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<NotificationDashboard>(`/admin/notifications/dashboard${queryString}`);
  },

  // Send notification
  send: (data: NotificationCreateRequest): Promise<ApiResponse<{
    notification: Notification;
    deliveryStats: {
      targetedUsers: number;
      successfulSends: number;
      failedSends: number;
    };
  }>> =>
    fetchAPI<{
      notification: Notification;
      deliveryStats: {
        targetedUsers: number;
        successfulSends: number;
        failedSends: number;
      };
    }>('/admin/notifications/send', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Send test notification
  sendTest: (data: {
    fcmToken: string;
    title: string;
    body: string;
    image?: string;
    data?: Record<string, any>;
  }): Promise<ApiResponse<{
    messageId: string;
    fcmToken: string;
  }>> =>
    fetchAPI<{
      messageId: string;
      fcmToken: string;
    }>('/admin/notifications/test', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Preview notification
  preview: (data: {
    targetType: 'all' | 'specific' | 'category' | 'location';
    targetUsers?: string[];
    targetCategory?: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
    locationFilter?: {
      cities?: string[];
      states?: string[];
      pincodes?: string[];
    };
  }): Promise<ApiResponse<NotificationPreview>> =>
    fetchAPI<NotificationPreview>('/admin/notifications/preview', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Get all notifications
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
    targetType?: 'all' | 'specific' | 'category' | 'location';
    targetCategory?: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<ApiResponse<NotificationPaginatedResponse>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.status) queryParams.push(`status=${params.status}`);
      if (params.targetType) queryParams.push(`targetType=${params.targetType}`);
      if (params.targetCategory) queryParams.push(`targetCategory=${params.targetCategory}`);
      if (params.startDate) queryParams.push(`startDate=${params.startDate}`);
      if (params.endDate) queryParams.push(`endDate=${params.endDate}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<NotificationPaginatedResponse>(`/admin/notifications${queryString}`);
  },

  // Get notification by ID
  getById: (id: string): Promise<ApiResponse<{
    notification: Notification;
    analytics: {
      deliveryRate: string;
      successRate: string;
      failureRate: string;
      clickThroughRate: string;
      openRate: string;
    };
  }>> =>
    fetchAPI<{
      notification: Notification;
      analytics: {
        deliveryRate: string;
        successRate: string;
        failureRate: string;
        clickThroughRate: string;
        openRate: string;
      };
    }>(`/admin/notifications/${id}`),

  // Get user statistics
  getUserStats: (): Promise<ApiResponse<NotificationUserStats>> =>
    fetchAPI<NotificationUserStats>('/admin/notifications/user-stats'),

  // Get target users
  getTargetUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    city?: string;
    state?: string;
    targetCategory?: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
  }): Promise<ApiResponse<{
    users: Array<{
      _id: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      addresses: Array<{
        city: string;
        state: string;
        postalCode: string;
      }>;
      notificationPreferences: Record<string, boolean>;
      lastLogin: string;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }>> => {
    const queryParams = [];
    if (params) {
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
      if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      if (params.city) queryParams.push(`city=${encodeURIComponent(params.city)}`);
      if (params.state) queryParams.push(`state=${encodeURIComponent(params.state)}`);
      if (params.targetCategory) queryParams.push(`targetCategory=${params.targetCategory}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return fetchAPI<{
      users: Array<{
        _id: string;
        firstName: string;
        lastName: string;
        phoneNumber: string;
        addresses: Array<{
          city: string;
          state: string;
          postalCode: string;
        }>;
        notificationPreferences: Record<string, boolean>;
        lastLogin: string;
      }>;
      pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
      };
    }>(`/admin/notifications/target-users${queryString}`);
  },

  // Clean invalid FCM tokens
  cleanTokens: (): Promise<ApiResponse<{
    totalChecked: number;
    validTokens: number;
    invalidTokens: number;
  }>> =>
    fetchAPI<{
      totalChecked: number;
      validTokens: number;
      invalidTokens: number;
    }>('/admin/notifications/clean-tokens', {
      method: 'POST'
    }),

  // Delete notification
  delete: (id: string): Promise<ApiResponse<{
    deletedId: string;
  }>> =>
    fetchAPI<{
      deletedId: string;
    }>(`/admin/notifications/${id}`, {
      method: 'DELETE'
    }),

  // Template management
  templates: {
    // Get all templates
    getAll: (params?: {
      page?: number;
      limit?: number;
      category?: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates';
      type?: 'manual' | 'automatic' | 'scheduled';
      isActive?: boolean;
      search?: string;
    }): Promise<ApiResponse<NotificationTemplatePaginatedResponse>> => {
      const queryParams = [];
      if (params) {
        if (params.page) queryParams.push(`page=${params.page}`);
        if (params.limit) queryParams.push(`limit=${params.limit}`);
        if (params.category) queryParams.push(`category=${params.category}`);
        if (params.type) queryParams.push(`type=${params.type}`);
        if (params.isActive !== undefined) queryParams.push(`isActive=${params.isActive}`);
        if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);
      }

      const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
      return fetchAPI<NotificationTemplatePaginatedResponse>(`/admin/notifications/templates${queryString}`);
    },

    // Get template by ID
    getById: (id: string): Promise<ApiResponse<{
      template: NotificationTemplate;
    }>> =>
      fetchAPI<{
        template: NotificationTemplate;
      }>(`/admin/notifications/templates/${id}`),

    // Create template
    create: (data: NotificationTemplateCreateRequest): Promise<ApiResponse<{
      template: NotificationTemplate;
    }>> =>
      fetchAPI<{
        template: NotificationTemplate;
      }>('/admin/notifications/templates', {
        method: 'POST',
        body: JSON.stringify(data)
      }),

    // Update template
    update: (id: string, data: Partial<NotificationTemplateCreateRequest>): Promise<ApiResponse<{
      template: NotificationTemplate;
    }>> =>
      fetchAPI<{
        template: NotificationTemplate;
      }>(`/admin/notifications/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      }),

    // Delete template
    delete: (id: string): Promise<ApiResponse<{
      deletedId: string;
    }>> =>
      fetchAPI<{
        deletedId: string;
      }>(`/admin/notifications/templates/${id}`, {
        method: 'DELETE'
      }),

    // Send from template
    sendFromTemplate: (id: string, data: {
      variables: Record<string, string>;
      targetType: 'all' | 'specific' | 'category' | 'location';
      targetUsers?: string[];
      data?: Record<string, any>;
    }): Promise<ApiResponse<{
      notification: Notification;
      deliveryStats: {
        targetedUsers: number;
        successfulSends: number;
        failedSends: number;
      };
    }>> =>
      fetchAPI<{
        notification: Notification;
        deliveryStats: {
          targetedUsers: number;
          successfulSends: number;
          failedSends: number;
        };
      }>(`/admin/notifications/templates/${id}/send`, {
        method: 'POST',
        body: JSON.stringify(data)
      })
  }
};

// Analytics API Interfaces
export interface DashboardAnalytics {
  period: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  overview: {
    orders: {
      totalOrders: number;
      avgOrderValue: number;
      statusDistribution: Record<string, number>;
      ordersPerDay: Array<{
        date: string;
        count: number;
        total: number;
      }>;
    };
    revenue: {
      totalRevenue: number;
      trends: Array<{
        date: string;
        revenue: number;
        orderCount: number;
        avgOrderValue: number;
      }>;
    };
    suppliers: {
      totalSuppliers: number;
      activeSuppliers: number;
      verifiedSuppliers: number;
      suppliersWithOrders: number;
      inactiveSuppliers: number;
    };
    users: {
      totalUsers: number;
      verifiedUsers: number;
      usersWithOrders: number;
      unverifiedUsers: number;
    };
    transporters: {
      totalTransporters: number;
      activeTransporters: number;
      verifiedTransporters: number;
      inactiveTransporters: number;
    };
    products: {
      totalProducts: number;
      totalCategories: number;
      productsWithOrders: number;
      productsWithoutOrders: number;
    };
  };
  trends: {
    recentOrders: Array<{
      _id: string;
      orderNumber: string;
      status: string;
      totalAmount: number;
      createdAt: string;
      trader?: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
      } | null;
      transporter?: {
        name: string;
        companyName?: string;
      };
      supplier?: {
        businessName: string;
        ownerName: string;
      };
    }>;
    topSuppliers: Array<{
      id: string;
      name: string;
      orderCount: number;
      totalValue: number;
    }>;
    orderStatusDistribution: Array<{
      status: string;
      count: number;
    }>;
  };
}

export interface RevenueAnalytics {
  period: string;
  groupBy: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  data: {
    totalRevenue: number;
    trends: Array<{
      date: string;
      revenue: number;
      orderCount: number;
      avgOrderValue: number;
    }>;
  };
}

export interface OrdersAnalytics {
  period: string;
  filters: {
    status: string | null;
    supplier: string | null;
    transporter: string | null;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
  data: {
    totalOrders: number;
    avgOrderValue: number;
    statusDistribution: Record<string, number>;
    ordersPerDay: Array<{
      date: string;
      count: number;
      total: number;
    }>;
  };
}

export interface RecentOrders {
  orders: Array<{
    _id: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    createdAt: string;
    trader?: {
      firstName: string;
      lastName: string;
      phoneNumber: string;
    } | null;
    transporter?: {
      name: string;
      companyName?: string;
    };
    supplier?: {
      businessName: string;
      ownerName: string;
    };
  }>;
  totalCount: number;
  filters: {
    status: string | null;
    supplier: string | null;
    transporter: string | null;
  };
}

export interface OrderDetails {
  order: {
    _id: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    subtotal: number;
    taxAmount: number;
    deliveryCharge: number;
    discountAmount: number;
    createdAt: string;
    expectedDeliveryDate?: string;
    actualDeliveryDate?: string;
    trader: {
      firstName: string;
      lastName: string;
      phoneNumber: string;
      email?: string;
    };
    transporter?: {
      name: string;
      mobile: string;
      email?: string;
      companyName?: string;
    };
    assignedSupplier?: {
      businessName: string;
      ownerName: string;
      email: string;
      mobile: string;
      address: {
        street: string;
        city: string;
        state: string;
        pincode: string;
      };
    };
    items: Array<{
      product: {
        _id: string;
        name: string;
        description?: string;
        images: Array<{
          url: string;
          isDefault: boolean;
        }>;
        unit: string;
      };
      quantity: number;
      unit: string;
      basePrice: number;
      pricePerUnit: number;
      totalPrice: number;
      supplierInfo?: {
        name: string;
        email: string;
        mobile: string;
      };
    }>;
    deliveryAddress: {
      name: string;
      street: string;
      city: string;
      state: string;
      pincode: string;
    };
    statusHistory: Array<{
      status: string;
      timestamp: string;
      updatedBy: string;
      userType: string;
      notes?: string;
    }>;
  };
}

export interface SuppliersAnalytics {
  period: string;
  filters: {
    status: string | null;
    verificationStatus: string | null;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
  data: {
    totalSuppliers: number;
    activeSuppliers: number;
    verifiedSuppliers: number;
    suppliersWithOrders: number;
    inactiveSuppliers: number;
  };
}

export interface SupplierDetails {
  supplier: {
    _id: string;
    businessName: string;
    ownerName: string;
    email: string;
    mobile: string;
    isActive: boolean;
    verificationStatus: string;
    address: {
      street: string;
      city: string;
      state: string;
      pincode: string;
    };
    products: Array<{
      _id: string;
      name: string;
      price: number;
      unit: string;
    }>;
    orderStats: {
      totalOrders: number;
      completedOrders: number;
      completionRate: number;
      totalRevenue: number;
      avgOrderValue: number;
    };
    createdAt: string;
    updatedAt: string;
  };
}

export interface MobileUsersAnalytics {
  period: string;
  filters: {
    isPhoneVerified: boolean | null;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
  data: {
    totalUsers: number;
    verifiedUsers: number;
    usersWithOrders: number;
    unverifiedUsers: number;
  };
}

export interface MobileUserDetails {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email?: string;
    isPhoneVerified: boolean;
    profilePicture?: string;
    addresses: Array<{
      name: string;
      phoneNumber: string;
      street: string;
      city: string;
      state: string;
      pincode: string;
      isDefault: boolean;
    }>;
    orderStats: {
      totalOrders: number;
      completedOrders: number;
      completionRate: number;
      totalSpent: number;
      avgOrderValue: number;
    };
    lastLogin?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface TransportersAnalytics {
  period: string;
  filters: {
    status: string | null;
    verificationStatus: string | null;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
  data: {
    totalTransporters: number;
    activeTransporters: number;
    verifiedTransporters: number;
    inactiveTransporters: number;
  };
}

export interface ProductsAnalytics {
  period: string;
  filters: {
    category: string | null;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
  data: {
    totalProducts: number;
    totalCategories: number;
    productsWithOrders: number;
    productsWithoutOrders: number;
  };
}

// Analytics API
export const AnalyticsAPI = {
  // Dashboard Analytics
  getDashboardAnalytics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<DashboardAnalytics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    return fetchAPI<DashboardAnalytics>(`/analytics/dashboard?${queryParams.toString()}`);
  },

  // Revenue Analytics
  getRevenueAnalytics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }): Promise<ApiResponse<RevenueAnalytics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);

    return fetchAPI<RevenueAnalytics>(`/analytics/revenue?${queryParams.toString()}`);
  },

  // Orders Analytics
  getOrdersAnalytics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    startDate?: string;
    endDate?: string;
    status?: string;
    supplier?: string;
    transporter?: string;
  }): Promise<ApiResponse<OrdersAnalytics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.supplier) queryParams.append('supplier', params.supplier);
    if (params?.transporter) queryParams.append('transporter', params.transporter);

    return fetchAPI<OrdersAnalytics>(`/analytics/orders?${queryParams.toString()}`);
  },

  // Recent Orders
  getRecentOrders: (params?: {
    limit?: number;
    status?: string;
    supplier?: string;
    transporter?: string;
  }): Promise<ApiResponse<RecentOrders>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.supplier) queryParams.append('supplier', params.supplier);
    if (params?.transporter) queryParams.append('transporter', params.transporter);

    return fetchAPI<RecentOrders>(`/analytics/recent-orders?${queryParams.toString()}`);
  },

  // Order Details
  getOrderDetails: (orderId: string): Promise<ApiResponse<OrderDetails>> => {
    return fetchAPI<OrderDetails>(`/analytics/orders/${orderId}`);
  },

  // Suppliers Analytics
  getSuppliersAnalytics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    status?: 'active' | 'inactive';
    verificationStatus?: 'pending' | 'verified' | 'rejected';
  }): Promise<ApiResponse<SuppliersAnalytics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.verificationStatus) queryParams.append('verificationStatus', params.verificationStatus);

    return fetchAPI<SuppliersAnalytics>(`/analytics/suppliers?${queryParams.toString()}`);
  },

  // Supplier Details
  getSupplierDetails: (supplierId: string): Promise<ApiResponse<SupplierDetails>> => {
    return fetchAPI<SupplierDetails>(`/analytics/suppliers/${supplierId}`);
  },

  // Mobile Users Analytics
  getMobileUsersAnalytics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    isPhoneVerified?: boolean;
  }): Promise<ApiResponse<MobileUsersAnalytics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.isPhoneVerified !== undefined) queryParams.append('isPhoneVerified', params.isPhoneVerified.toString());

    return fetchAPI<MobileUsersAnalytics>(`/analytics/mobile-users?${queryParams.toString()}`);
  },

  // Mobile User Details
  getMobileUserDetails: (userId: string): Promise<ApiResponse<MobileUserDetails>> => {
    return fetchAPI<MobileUserDetails>(`/analytics/mobile-users/${userId}`);
  },

  // Transporters Analytics
  getTransportersAnalytics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    status?: 'active' | 'inactive';
    verificationStatus?: 'pending' | 'verified' | 'rejected';
  }): Promise<ApiResponse<TransportersAnalytics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.verificationStatus) queryParams.append('verificationStatus', params.verificationStatus);

    return fetchAPI<TransportersAnalytics>(`/analytics/transporters?${queryParams.toString()}`);
  },

  // Products Analytics
  getProductsAnalytics: (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    category?: string;
  }): Promise<ApiResponse<ProductsAnalytics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.category) queryParams.append('category', params.category);

    return fetchAPI<ProductsAnalytics>(`/analytics/products?${queryParams.toString()}`);
  },
};

// Banner Management API
// Consolidated Banner Management API - matches API documentation exactly
export const BannerAPI = {
  // Get all banners with filters and pagination
  getAllBanners: (params?: {
    page?: number;
    limit?: number;
    position?: string;
    isActive?: boolean;
    targetAudience?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    banners: Banner[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    summary: {
      totalBanners: number;
      activeBanners: number;
      totalViews: number;
      totalClicks: number;
      totalConversions: number;
      avgClickThroughRate: number;
    };
    filters: any;
  }>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.position) queryParams.append('position', params.position);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.targetAudience) queryParams.append('targetAudience', params.targetAudience);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return fetchAPI(`/admin/banners?${queryParams.toString()}`);
  },

  // Get banner by ID with populated references
  getBanner: (id: string): Promise<ApiResponse<{ banner: Banner }>> =>
    fetchAPI(`/admin/banners/${id}`),

  // Create banner (multipart/form-data)
  createBanner: (bannerData: BannerCreateRequest): Promise<ApiResponse<{ banner: Banner }>> => {
    const formData = new FormData();
    formData.append('title', bannerData.title);
    if (bannerData.description) formData.append('description', bannerData.description);
    formData.append('actionType', bannerData.actionType);
    formData.append('actionData', bannerData.actionData);
    formData.append('position', bannerData.position);
    formData.append('priority', bannerData.priority.toString());
    formData.append('displayOrder', bannerData.displayOrder.toString());
    formData.append('targetAudience', bannerData.targetAudience);
    formData.append('locationTargeting', bannerData.locationTargeting);
    formData.append('isActive', bannerData.isActive.toString());
    formData.append('startDate', bannerData.startDate);
    formData.append('endDate', bannerData.endDate);
    formData.append('tags', bannerData.tags);
    if (bannerData.notes) formData.append('notes', bannerData.notes);
    formData.append('image', bannerData.image);

    return fetchFormAPI('/admin/banners', formData, 'POST');
  },

  // Update banner (multipart optional)
  updateBanner: (id: string, bannerData: Partial<BannerCreateRequest>): Promise<ApiResponse<{ banner: Banner }>> => {
    const formData = new FormData();
    if (bannerData.title) formData.append('title', bannerData.title);
    if (bannerData.description) formData.append('description', bannerData.description);
    if (bannerData.actionType) formData.append('actionType', bannerData.actionType);
    if (bannerData.actionData) formData.append('actionData', bannerData.actionData);
    if (bannerData.position) formData.append('position', bannerData.position);
    if (bannerData.priority !== undefined) formData.append('priority', bannerData.priority.toString());
    if (bannerData.displayOrder !== undefined) formData.append('displayOrder', bannerData.displayOrder.toString());
    if (bannerData.targetAudience) formData.append('targetAudience', bannerData.targetAudience);
    if (bannerData.locationTargeting) formData.append('locationTargeting', bannerData.locationTargeting);
    if (bannerData.isActive !== undefined) formData.append('isActive', bannerData.isActive.toString());
    if (bannerData.startDate) formData.append('startDate', bannerData.startDate);
    if (bannerData.endDate) formData.append('endDate', bannerData.endDate);
    if (bannerData.tags) formData.append('tags', bannerData.tags);
    if (bannerData.notes) formData.append('notes', bannerData.notes);
    if (bannerData.image) formData.append('image', bannerData.image);

    return fetchFormAPI(`/admin/banners/${id}`, formData, 'PUT');
  },

  // Delete banner
  deleteBanner: (id: string): Promise<ApiResponse<{ deletedId: string }>> =>
    fetchAPI(`/admin/banners/${id}`, { method: 'DELETE' }),

  // Toggle banner active status
  toggleBannerStatus: (id: string): Promise<ApiResponse<{ banner: Banner }>> =>
    fetchAPI(`/admin/banners/${id}/toggle-active`, { method: 'PATCH' }),

  // Get banner analytics with period filter
  getBannerAnalytics: (id: string, period: '24h' | '7d' | '30d' | '90d' = '7d'): Promise<ApiResponse<{
    banner: Banner;
    analytics: {
      overview: {
        totalViews: number;
        totalClicks: number;
        totalConversions: number;
        clickThroughRate: string;
        conversionRate: string;
      };
      performance: {
        isActive: boolean;
        position: string;
        priority: number;
        targetAudience: string;
        startDate: string;
        endDate: string | null;
      };
      targeting: {
        locationTargeting: any;
        tags: string[];
      };
    };
    period: string;
  }>> =>
    fetchAPI(`/admin/banners/${id}/analytics?period=${period}`),

  // Get banner statistics dashboard
  getBannerStatistics: (period: '24h' | '7d' | '30d' | '90d' = '7d'): Promise<ApiResponse<{
    period: string;
    overview: {
      totalBanners: number;
      activeBanners: number;
      totalViews: number;
      totalClicks: number;
      totalConversions: number;
      avgClickThroughRate: number;
    };
    byPosition: Array<{
      _id: string;
      count: number;
      activeCount: number;
    }>;
    topPerforming: Array<{
      title: string;
      analytics: {
        views: number;
        clicks: number;
      };
      clickThroughRate: number;
    }>;
    recentBanners: Array<{
      title: string;
      position: string;
      isActive: boolean;
      analytics: {
        views: number;
      };
    }>;
  }>> =>
    fetchAPI(`/admin/banners/statistics?period=${period}`),

  // Get resources for dropdowns (products, categories, offers, suppliers)
  getResources: (type?: 'products' | 'categories' | 'offers' | 'suppliers'): Promise<ApiResponse<{
    products?: Array<{
      _id: string;
      name: string;
      image: string;
      basePrice: number;
      category: {
        _id: string;
        name: string;
      };
    }>;
    categories?: Array<{
      _id: string;
      name: string;
      image: string;
      level: number;
      parent: string | null;
    }>;
    offers?: Array<{
      _id: string;
      title: string;
      description: string;
      offerType: string;
      value: number;
      validFrom: string;
      validTo: string;
    }>;
    suppliers?: Array<{
      _id: string;
      businessName: string;
      ownerName: string;
      email: string;
      location: any;
    }>;
  }>> => {
    const queryParams = type ? `?type=${type}` : '';
    return fetchAPI(`/admin/banners/resources${queryParams}`);
  },
};

// Offer Management API
export const OfferAPI = {
  // Get all offers with filters
  getAllOffers: (filters?: OfferFilter): Promise<ApiResponse<PaginatedResponse<Offer>>> => {
    const queryParams = new URLSearchParams();
    if (filters?.page) queryParams.append('page', filters.page.toString());
    if (filters?.limit) queryParams.append('limit', filters.limit.toString());
    if (filters?.offerType) queryParams.append('offerType', filters.offerType);
    if (filters?.applicableOn) queryParams.append('applicableOn', filters.applicableOn);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.targetAudience) queryParams.append('targetAudience', filters.targetAudience);
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
    if (filters?.validFrom) queryParams.append('validFrom', filters.validFrom);
    if (filters?.validTo) queryParams.append('validTo', filters.validTo);

    return fetchAPI<PaginatedResponse<Offer>>(`/admin/offers?${queryParams.toString()}`);
  },

  // Get offer by ID
  getOffer: (id: string): Promise<ApiResponse<Offer>> =>
    fetchAPI<Offer>(`/admin/offers/${id}`),

  // Create offer
  createOffer: (offerData: OfferCreateRequest): Promise<ApiResponse<Offer>> => {
    const formData = new FormData();
    formData.append('title', offerData.title);
    if (offerData.description) formData.append('description', offerData.description);
    if (offerData.terms) formData.append('terms', offerData.terms);
    formData.append('offerType', offerData.offerType);
    formData.append('value', offerData.value.toString());
    if (offerData.maxDiscount) formData.append('maxDiscount', offerData.maxDiscount.toString());
    if (offerData.minOrderValue) formData.append('minOrderValue', offerData.minOrderValue.toString());
    formData.append('applicableOn', offerData.applicableOn);
    formData.append('targetAudience', offerData.targetAudience);
    formData.append('locationTargeting', offerData.locationTargeting);
    formData.append('usageLimit', offerData.usageLimit);
    formData.append('isActive', offerData.isActive.toString());
    formData.append('validFrom', offerData.validFrom);
    formData.append('validTo', offerData.validTo);
    formData.append('priority', offerData.priority.toString());
    formData.append('showOnHomepage', offerData.showOnHomepage.toString());
    formData.append('showInCart', offerData.showInCart.toString());
    formData.append('showInCheckout', offerData.showInCheckout.toString());
    if (offerData.promoCode) formData.append('promoCode', offerData.promoCode);
    formData.append('requiresPromoCode', offerData.requiresPromoCode.toString());
    formData.append('tags', offerData.tags);
    if (offerData.notes) formData.append('notes', offerData.notes);
    if (offerData.image) formData.append('image', offerData.image);

    return fetchFormAPI<Offer>('/admin/offers', formData);
  },

  // Update offer
  updateOffer: (id: string, offerData: Partial<OfferCreateRequest>): Promise<ApiResponse<Offer>> => {
    const formData = new FormData();
    if (offerData.title) formData.append('title', offerData.title);
    if (offerData.description) formData.append('description', offerData.description);
    if (offerData.terms) formData.append('terms', offerData.terms);
    if (offerData.offerType) formData.append('offerType', offerData.offerType);
    if (offerData.value !== undefined) formData.append('value', offerData.value.toString());
    if (offerData.maxDiscount !== undefined) formData.append('maxDiscount', offerData.maxDiscount.toString());
    if (offerData.minOrderValue !== undefined) formData.append('minOrderValue', offerData.minOrderValue.toString());
    if (offerData.applicableOn) formData.append('applicableOn', offerData.applicableOn);
    if (offerData.targetAudience) formData.append('targetAudience', offerData.targetAudience);
    if (offerData.locationTargeting) formData.append('locationTargeting', offerData.locationTargeting);
    if (offerData.usageLimit) formData.append('usageLimit', offerData.usageLimit);
    if (offerData.isActive !== undefined) formData.append('isActive', offerData.isActive.toString());
    if (offerData.validFrom) formData.append('validFrom', offerData.validFrom);
    if (offerData.validTo) formData.append('validTo', offerData.validTo);
    if (offerData.priority !== undefined) formData.append('priority', offerData.priority.toString());
    if (offerData.showOnHomepage !== undefined) formData.append('showOnHomepage', offerData.showOnHomepage.toString());
    if (offerData.showInCart !== undefined) formData.append('showInCart', offerData.showInCart.toString());
    if (offerData.showInCheckout !== undefined) formData.append('showInCheckout', offerData.showInCheckout.toString());
    if (offerData.promoCode) formData.append('promoCode', offerData.promoCode);
    if (offerData.requiresPromoCode !== undefined) formData.append('requiresPromoCode', offerData.requiresPromoCode.toString());
    if (offerData.tags) formData.append('tags', offerData.tags);
    if (offerData.notes) formData.append('notes', offerData.notes);
    if (offerData.image) formData.append('image', offerData.image);

    return fetchFormAPI<Offer>(`/admin/offers/${id}`, formData, 'PUT');
  },

  // Delete offer
  deleteOffer: (id: string): Promise<ApiResponse<{ deletedId: string }>> =>
    fetchAPI<{ deletedId: string }>(`/admin/offers/${id}`, 'DELETE'),

  // Toggle offer status
  toggleOfferStatus: (id: string): Promise<ApiResponse<Offer>> =>
    fetchAPI<Offer>(`/admin/offers/${id}/toggle-active`, 'PATCH'),

  // Get offer analytics
  getOfferAnalytics: (id: string, period: string = '7d'): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/offers/${id}/analytics?period=${period}`),

  // Get offer statistics
  getOfferStatistics: (period: string = '7d'): Promise<ApiResponse<OfferStatistics>> =>
    fetchAPI<OfferStatistics>(`/admin/offers/statistics?period=${period}`),

  // Get targeting options
  getTargetingOptions: (): Promise<ApiResponse<{ products: any[]; categories: any[] }>> =>
    fetchAPI<{ products: any[]; categories: any[] }>('/admin/offers/targeting-options'),
};

// Featured Deals Management API
export const FeaturedDealAPI = {
  // Get all featured deals with filters
  getAllFeaturedDeals: (filters?: FeaturedDealFilter): Promise<ApiResponse<PaginatedResponse<FeaturedDeal>>> => {
    const queryParams = new URLSearchParams();
    if (filters?.page) queryParams.append('page', filters.page.toString());
    if (filters?.limit) queryParams.append('limit', filters.limit.toString());
    if (filters?.dealType) queryParams.append('dealType', filters.dealType);
    if (filters?.displaySection) queryParams.append('displaySection', filters.displaySection);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.targetAudience) queryParams.append('targetAudience', filters.targetAudience);
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
    if (filters?.flashSaleOnly) queryParams.append('flashSaleOnly', filters.flashSaleOnly.toString());

    return fetchAPI<PaginatedResponse<FeaturedDeal>>(`/admin/featured-deals?${queryParams.toString()}`);
  },

  // Get featured deal by ID
  getFeaturedDeal: (id: string): Promise<ApiResponse<FeaturedDeal>> =>
    fetchAPI<FeaturedDeal>(`/admin/featured-deals/${id}`),

  // Create featured deal
  createFeaturedDeal: (dealData: FeaturedDealCreateRequest): Promise<ApiResponse<FeaturedDeal>> => {
    const formData = new FormData();
    formData.append('title', dealData.title);
    if (dealData.subtitle) formData.append('subtitle', dealData.subtitle);
    if (dealData.description) formData.append('description', dealData.description);
    if (dealData.badge) formData.append('badge', dealData.badge);
    if (dealData.badgeColor) formData.append('badgeColor', dealData.badgeColor);
    formData.append('dealType', dealData.dealType);
    formData.append('featuredProducts', dealData.featuredProducts);
    if (dealData.bundleConfig) formData.append('bundleConfig', dealData.bundleConfig);
    formData.append('targetCategories', dealData.targetCategories);
    if (dealData.categoryDiscount !== undefined) formData.append('categoryDiscount', dealData.categoryDiscount.toString());
    formData.append('displaySection', dealData.displaySection);
    formData.append('priority', dealData.priority.toString());
    formData.append('displayOrder', dealData.displayOrder.toString());
    formData.append('targetAudience', dealData.targetAudience);
    formData.append('locationTargeting', dealData.locationTargeting);
    formData.append('isActive', dealData.isActive.toString());
    formData.append('startDate', dealData.startDate);
    formData.append('endDate', dealData.endDate);
    if (dealData.flashSaleConfig) formData.append('flashSaleConfig', dealData.flashSaleConfig);
    if (dealData.ctaText) formData.append('ctaText', dealData.ctaText);
    if (dealData.ctaLink) formData.append('ctaLink', dealData.ctaLink);
    if (dealData.terms) formData.append('terms', dealData.terms);
    if (dealData.seoTitle) formData.append('seoTitle', dealData.seoTitle);
    if (dealData.seoDescription) formData.append('seoDescription', dealData.seoDescription);
    formData.append('tags', dealData.tags);
    if (dealData.notes) formData.append('notes', dealData.notes);
    formData.append('image', dealData.image);

    return fetchFormAPI<FeaturedDeal>('/admin/featured-deals', formData);
  },

  // Update featured deal
  updateFeaturedDeal: (id: string, dealData: Partial<FeaturedDealCreateRequest>): Promise<ApiResponse<FeaturedDeal>> => {
    const formData = new FormData();
    if (dealData.title) formData.append('title', dealData.title);
    if (dealData.subtitle) formData.append('subtitle', dealData.subtitle);
    if (dealData.description) formData.append('description', dealData.description);
    if (dealData.badge) formData.append('badge', dealData.badge);
    if (dealData.badgeColor) formData.append('badgeColor', dealData.badgeColor);
    if (dealData.dealType) formData.append('dealType', dealData.dealType);
    if (dealData.featuredProducts) formData.append('featuredProducts', dealData.featuredProducts);
    if (dealData.bundleConfig) formData.append('bundleConfig', dealData.bundleConfig);
    if (dealData.targetCategories) formData.append('targetCategories', dealData.targetCategories);
    if (dealData.categoryDiscount !== undefined) formData.append('categoryDiscount', dealData.categoryDiscount.toString());
    if (dealData.displaySection) formData.append('displaySection', dealData.displaySection);
    if (dealData.priority !== undefined) formData.append('priority', dealData.priority.toString());
    if (dealData.displayOrder !== undefined) formData.append('displayOrder', dealData.displayOrder.toString());
    if (dealData.targetAudience) formData.append('targetAudience', dealData.targetAudience);
    if (dealData.locationTargeting) formData.append('locationTargeting', dealData.locationTargeting);
    if (dealData.isActive !== undefined) formData.append('isActive', dealData.isActive.toString());
    if (dealData.startDate) formData.append('startDate', dealData.startDate);
    if (dealData.endDate) formData.append('endDate', dealData.endDate);
    if (dealData.flashSaleConfig) formData.append('flashSaleConfig', dealData.flashSaleConfig);
    if (dealData.ctaText) formData.append('ctaText', dealData.ctaText);
    if (dealData.ctaLink) formData.append('ctaLink', dealData.ctaLink);
    if (dealData.terms) formData.append('terms', dealData.terms);
    if (dealData.seoTitle) formData.append('seoTitle', dealData.seoTitle);
    if (dealData.seoDescription) formData.append('seoDescription', dealData.seoDescription);
    if (dealData.tags) formData.append('tags', dealData.tags);
    if (dealData.notes) formData.append('notes', dealData.notes);
    if (dealData.image) formData.append('image', dealData.image);

    return fetchFormAPI<FeaturedDeal>(`/admin/featured-deals/${id}`, formData, 'PUT');
  },

  // Delete featured deal
  deleteFeaturedDeal: (id: string): Promise<ApiResponse<{ deletedId: string }>> =>
    fetchAPI<{ deletedId: string }>(`/admin/featured-deals/${id}`, 'DELETE'),

  // Toggle deal status
  toggleDealStatus: (id: string): Promise<ApiResponse<FeaturedDeal>> =>
    fetchAPI<FeaturedDeal>(`/admin/featured-deals/${id}/toggle-active`, 'PATCH'),

  // Get deal analytics
  getDealAnalytics: (id: string, period: string = '7d'): Promise<ApiResponse<any>> =>
    fetchAPI<any>(`/admin/featured-deals/${id}/analytics?period=${period}`),

  // Get deal statistics
  getDealStatistics: (period: string = '7d'): Promise<ApiResponse<FeaturedDealStatistics>> =>
    fetchAPI<FeaturedDealStatistics>(`/admin/featured-deals/statistics?period=${period}`),

  // Duplicate deal
  duplicateDeal: (id: string): Promise<ApiResponse<FeaturedDeal>> =>
    fetchAPI<FeaturedDeal>(`/admin/featured-deals/${id}/duplicate`, 'POST'),
};

// Banners, Offers & Featured Deals API
// Note: Main interfaces are in types.ts

// Response interfaces for paginated data
interface BannerPaginatedResponse {
  banners: any[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: {
    totalBanners: number;
    activeBanners: number;
    totalViews: number;
    totalClicks: number;
    totalConversions: number;
    avgClickThroughRate: number;
  };
}

interface OfferPaginatedResponse {
  offers: any[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: {
    totalOffers: number;
    activeOffers: number;
    totalApplications: number;
    totalSavings: number;
    avgSuccessRate: number;
  };
}

interface FeaturedDealPaginatedResponse {
  deals: any[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: {
    totalDeals: number;
    activeDeals: number;
    totalViews: number;
    totalClicks: number;
    totalPurchases: number;
    totalRevenue: number;
    avgConversionRate: number;
  };
}

// Banners API
// Legacy alias for backward compatibility - use BannerAPI instead
export const BannersAPI = BannerAPI;

// Offers API
export const OffersAPI = {
  // Get all offers
  getAllOffers: (params?: {
    page?: number;
    limit?: number;
    offerType?: string;
    applicableOn?: string;
    isActive?: boolean;
    targetAudience?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<OfferPaginatedResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offerType) queryParams.append('offerType', params.offerType);
    if (params?.applicableOn) queryParams.append('applicableOn', params.applicableOn);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.targetAudience) queryParams.append('targetAudience', params.targetAudience);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return fetchAPI<OfferPaginatedResponse>(`/admin/offers?${queryParams.toString()}`);
  },

  // Get offer by ID
  getOfferById: (id: string): Promise<ApiResponse<{ offer: any }>> => {
    return fetchAPI<{ offer: any }>(`/admin/offers/${id}`);
  },

  // Create offer
  createOffer: (formData: FormData): Promise<ApiResponse<{ offer: any }>> => {
    return fetchFormAPI<{ offer: any }>('/admin/offers', formData, 'POST');
  },

  // Update offer
  updateOffer: (id: string, formData: FormData): Promise<ApiResponse<{ offer: any }>> => {
    return fetchFormAPI<{ offer: any }>(`/admin/offers/${id}`, formData, 'PUT');
  },

  // Delete offer
  deleteOffer: (id: string): Promise<ApiResponse<{ deletedId: string }>> => {
    return fetchAPI<{ deletedId: string }>(`/admin/offers/${id}`, { method: 'DELETE' });
  },

  // Toggle offer status
  toggleOfferStatus: (id: string): Promise<ApiResponse<{ offer: any }>> => {
    return fetchAPI<{ offer: any }>(`/admin/offers/${id}/toggle-active`, { method: 'PATCH' });
  },

  // Get offer analytics
  getOfferAnalytics: (id: string, period?: string): Promise<ApiResponse<any>> => {
    const queryParams = period ? `?period=${period}` : '';
    return fetchAPI<any>(`/admin/offers/${id}/analytics${queryParams}`);
  },

  // Get offer statistics
  getOfferStatistics: (period?: string): Promise<ApiResponse<any>> => {
    const queryParams = period ? `?period=${period}` : '';
    return fetchAPI<any>(`/admin/offers/statistics${queryParams}`);
  },

  // 🔗 HELPER API: Get targeting options (for dropdowns)
  getTargetingOptions: (params?: {
    type?: 'products' | 'categories' | 'suppliers';
    search?: string;
    limit?: number;
    page?: number;
  }): Promise<ApiResponse<{ products?: any[]; categories?: any[]; suppliers?: any[]; items?: any[] }>> => {
    if (!params || !params.type) {
      // Legacy: return all options
      return fetchAPI<{ products: any[]; categories: any[] }>('/admin/offers/targeting-options');
    }

    const queryParams = new URLSearchParams();
    queryParams.append('type', params.type);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());

    return fetchAPI<any>(`/admin/offers/targeting-options?${queryParams.toString()}`);
  },
};

// Featured Deals API
export const FeaturedDealsAPI = {
  // Get all featured deals
  getAllDeals: (params?: {
    page?: number;
    limit?: number;
    dealType?: string;
    displaySection?: string;
    isActive?: boolean;
    targetAudience?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    flashSaleOnly?: boolean;
  }): Promise<ApiResponse<FeaturedDealPaginatedResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.dealType) queryParams.append('dealType', params.dealType);
    if (params?.displaySection) queryParams.append('displaySection', params.displaySection);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.targetAudience) queryParams.append('targetAudience', params.targetAudience);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.flashSaleOnly !== undefined) queryParams.append('flashSaleOnly', params.flashSaleOnly.toString());

    return fetchAPI<FeaturedDealPaginatedResponse>(`/admin/featured-deals?${queryParams.toString()}`);
  },

  // Get deal by ID
  getDealById: (id: string): Promise<ApiResponse<{ deal: any }>> => {
    return fetchAPI<{ deal: any }>(`/admin/featured-deals/${id}`);
  },

  // Create deal
  createDeal: (formData: FormData): Promise<ApiResponse<{ deal: any }>> => {
    return fetchFormAPI<{ deal: any }>('/admin/featured-deals', formData, 'POST');
  },

  // Update deal
  updateDeal: (id: string, formData: FormData): Promise<ApiResponse<{ deal: any }>> => {
    return fetchFormAPI<{ deal: any }>(`/admin/featured-deals/${id}`, formData, 'PUT');
  },

  // Delete deal
  deleteDeal: (id: string): Promise<ApiResponse<{ deletedId: string }>> => {
    return fetchAPI<{ deletedId: string }>(`/admin/featured-deals/${id}`, { method: 'DELETE' });
  },

  // Toggle deal status
  toggleDealStatus: (id: string): Promise<ApiResponse<{ deal: any }>> => {
    return fetchAPI<{ deal: any }>(`/admin/featured-deals/${id}/toggle-active`, { method: 'PATCH' });
  },

  // Get deal analytics
  getDealAnalytics: (id: string, period?: string): Promise<ApiResponse<any>> => {
    const queryParams = period ? `?period=${period}` : '';
    return fetchAPI<any>(`/admin/featured-deals/${id}/analytics${queryParams}`);
  },

  // Get deal statistics
  getDealStatistics: (period?: string): Promise<ApiResponse<any>> => {
    const queryParams = period ? `?period=${period}` : '';
    return fetchAPI<any>(`/admin/featured-deals/statistics${queryParams}`);
  },

  // Duplicate deal
  duplicateDeal: (id: string): Promise<ApiResponse<{ deal: any }>> => {
    return fetchAPI<{ deal: any }>(`/admin/featured-deals/${id}/duplicate`, { method: 'POST' });
  },

  // 🔗 HELPER API: Get featured deal resources (for dropdowns)
  getDealResources: (params: {
    type: 'products' | 'categories';
    search?: string;
    limit?: number;
    page?: number;
  }): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('type', params.type);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());

    return fetchAPI<any>(`/admin/featured-deals/resources?${queryParams.toString()}`);
  },
};

// ============================================
// EARNINGS & PAYMENTS MANAGEMENT API
// ============================================

// Earnings interfaces
export interface EarningsDashboard {
  platformEarnings: {
    totalEarnings: number;
    paidEarnings: number;
    pendingEarnings: number;
    totalTransactions: number;
  };
  earningsBreakdown: {
    supplier: {
      totalEarnings: number;
      paidEarnings: number;
      pendingEarnings: number;
      totalTransactions: number;
    };
    transporter: {
      totalEarnings: number;
      paidEarnings: number;
      pendingEarnings: number;
      totalTransactions: number;
    };
    admin: {
      totalEarnings: number;
      paidEarnings: number;
      pendingEarnings: number;
      totalTransactions: number;
    };
  };
  recentEarnings: EarningsRecord[];
}

export interface EarningsRecord {
  _id: string;
  orderNumber: string;
  totalOrderValue: number;
  orderTotal?: number;
  supplierPayments: number;
  transporterPayments: number;
  platformEarnings: number;
  netEarnings: number;
  paymentStatus?: 'pending' | 'paid' | 'failed';
  earnedAt: string;
}

export interface PendingPayout {
  _id: string;
  entityId: string;
  entityType: 'supplier' | 'transporter';
  entityName: string;
  entityEmail: string;
  entityMobile: string;
  orderNumber: string;
  amount: number;
  earnedAt: string;
}

export interface PendingPayoutsResponse {
  payouts: PendingPayout[];
  summary: {
    supplier: {
      totalPendingAmount: number;
      totalTransactions: number;
    };
    transporter: {
      totalPendingAmount: number;
      totalTransactions: number;
    };
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface ProcessPayoutRequest {
  entityId: string;
  entityType: 'supplier' | 'transporter';
  earningIds: string[];
  paymentMethod: 'bank_transfer' | 'upi' | 'cash' | 'cheque' | 'wallet' | 'neft' | 'rtgs' | 'imps';
  transactionId: string;
  notes?: string;
  bankReference?: string;
  ifscCode?: string;
  region?: string;
}

export interface ProcessPayoutResponse {
  entityId: string;
  entityType: 'supplier' | 'transporter';
  totalPaidAmount: number;
  transactionsProcessed: number;
  earningIds: string[];
  paymentDetails: {
    paidAt: string;
    paidAmount: number;
    transactionId: string;
    paymentMethod: string;
    processedBy: string;
    notes?: string;
  };
}

export interface EarningsStatistics {
  totalPlatformEarnings: number;
  totalOrderValue: number;
  totalSupplierPayments: number;
  totalTransporterPayments: number;
  totalOrders: number;
  averageOrderEarnings: number;
  totalTransactions: number;
  earningsPercentage: number;
}

export interface EarningsHistoryResponse {
  earnings: EarningsRecord[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// Earnings Management API
export const EarningsAPI = {
  // Get payments overview (NEW)
  getOverview: (params?: {
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    entityType?: 'supplier' | 'transporter';
    region?: string;
  }): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.entityType) queryParams.append('entityType', params.entityType);
    if (params?.region) queryParams.append('region', params.region);

    return fetchAPI<any>(`/admin/earnings/overview?${queryParams.toString()}`);
  },

  // Get order breakdown (NEW)
  getOrderBreakdown: (orderId: string): Promise<ApiResponse<any>> => {
    return fetchAPI<any>(`/admin/earnings/order/${orderId}`);
  },

  // Get earnings dashboard
  getDashboard: (params?: {
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  }): Promise<ApiResponse<EarningsDashboard>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);

    return fetchAPI<EarningsDashboard>(`/admin/earnings/dashboard?${queryParams.toString()}`);
  },

  // Get earnings history (ENHANCED)
  getHistory: (params?: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'paid' | 'failed' | 'refunded';
    fromDate?: string;
    toDate?: string;
    entityType?: 'supplier' | 'transporter' | 'admin';
    region?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<EarningsHistoryResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params?.toDate) queryParams.append('toDate', params.toDate);
    if (params?.entityType) queryParams.append('entityType', params.entityType);
    if (params?.region) queryParams.append('region', params.region);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return fetchAPI<EarningsHistoryResponse>(`/admin/earnings/history?${queryParams.toString()}`);
  },

  // Get pending payouts (ENHANCED)
  getPendingPayouts: (params?: {
    entityType?: 'supplier' | 'transporter';
    page?: number;
    limit?: number;
    region?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<PendingPayoutsResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.entityType) queryParams.append('entityType', params.entityType);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.region) queryParams.append('region', params.region);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return fetchAPI<PendingPayoutsResponse>(`/admin/earnings/pending-payouts?${queryParams.toString()}`);
  },

  // Process payout (ENHANCED)
  processPayout: (payoutData: ProcessPayoutRequest): Promise<ApiResponse<ProcessPayoutResponse>> => {
    return fetchAPI<ProcessPayoutResponse>('/admin/earnings/process-payout', {
      method: 'POST',
      body: JSON.stringify(payoutData)
    });
  },

  // Get batch status (NEW)
  getBatchStatus: (params: {
    transactionId?: string;
    batchId?: string;
  }): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params.transactionId) queryParams.append('transactionId', params.transactionId);
    if (params.batchId) queryParams.append('batchId', params.batchId);

    return fetchAPI<any>(`/admin/earnings/batch-status?${queryParams.toString()}`);
  },

  // Get earnings statistics
  getStatistics: (params?: {
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  }): Promise<ApiResponse<EarningsStatistics>> => {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);

    return fetchAPI<EarningsStatistics>(`/admin/earnings/stats?${queryParams.toString()}`);
  },
};

// ============================================
// WITHDRAWALS MANAGEMENT API
// ============================================

// Withdrawal interfaces
export interface WithdrawalRequest {
  _id: string;
  requestId: string;
  entityId: string;
  entityType: 'supplier' | 'transporter';
  entityName: string;
  entityEmail: string;
  entityMobile: string;
  amount: number;
  status: 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'cancelled';
  bankDetails: {
    accountHolderName: string;
    accountNumberMasked: string;
    accountNumber?: string;
    ifscCode: string;
    bankName: string;
    branchName?: string;
    accountType: string;
  };
  requestedAt: string;
  approvedAt?: string;
  approvedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  processedAt?: string;
  completedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  adminNotes?: string;
  availableBalanceAtRequest?: number;
  earningsCount?: number;
  statusHistory?: {
    status: string;
    timestamp: string;
    notes?: string;
  }[];
  paymentDetails?: {
    transactionId: string;
    paymentMethod: string;
    bankReference?: string;
    paidAt: string;
    paidAmount: number;
  };
}

export interface WithdrawalsResponse {
  withdrawals: WithdrawalRequest[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  stats: {
    pending: { count: number; totalAmount: number };
    approved: { count: number; totalAmount: number };
    processing: { count: number; totalAmount: number };
    completed: { count: number; totalAmount: number };
    rejected: { count: number; totalAmount: number };
  };
}

export interface WithdrawalStats {
  byStatus: {
    pending: { count: number; totalAmount: number };
    approved: { count: number; totalAmount: number };
    processing: { count: number; totalAmount: number };
    completed: { count: number; totalAmount: number };
    rejected: { count: number; totalAmount: number };
    cancelled: { count: number; totalAmount: number };
  };
  byEntityType: {
    supplier: { count: number; totalAmount: number; completedAmount: number };
    transporter: { count: number; totalAmount: number; completedAmount: number };
  };
  dailyCompletions: {
    _id: string;
    count: number;
    totalAmount: number;
  }[];
}

// Withdrawals Management API
export const WithdrawalsAPI = {
  // Get all withdrawal requests with filters
  getAllWithdrawals: (params?: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'approved' | 'processing' | 'completed' | 'rejected';
    entityType?: 'supplier' | 'transporter';
    sortBy?: 'requestedAt' | 'amount' | 'entityName';
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }): Promise<ApiResponse<WithdrawalsResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.entityType) queryParams.append('entityType', params.entityType);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.search) queryParams.append('search', params.search);

    return fetchAPI<WithdrawalsResponse>(`/admin/withdrawals?${queryParams.toString()}`);
  },

  // Get withdrawal by ID
  getWithdrawalById: (id: string): Promise<ApiResponse<{
    withdrawal: WithdrawalRequest;
    relatedEarnings?: {
      _id: string;
      paymentId: string;
      orderNumber: string;
      amount: number;
      earnedAt: string;
    }[];
  }>> => {
    return fetchAPI(`/admin/withdrawals/${id}`);
  },

  // Approve withdrawal request
  approveWithdrawal: (id: string, notes?: string): Promise<ApiResponse<{
    withdrawal: WithdrawalRequest;
  }>> => {
    return fetchAPI(`/admin/withdrawals/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ notes })
    });
  },

  // Reject withdrawal request
  rejectWithdrawal: (id: string, reason: string): Promise<ApiResponse<{
    withdrawal: WithdrawalRequest;
  }>> => {
    return fetchAPI(`/admin/withdrawals/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason })
    });
  },

  // Start processing withdrawal
  processWithdrawal: (id: string, notes?: string): Promise<ApiResponse<{
    withdrawal: WithdrawalRequest;
  }>> => {
    return fetchAPI(`/admin/withdrawals/${id}/process`, {
      method: 'PUT',
      body: JSON.stringify({ notes })
    });
  },

  // Complete withdrawal
  completeWithdrawal: (id: string, paymentData: {
    transactionId: string;
    paymentMethod: 'bank_transfer' | 'upi' | 'neft' | 'rtgs' | 'imps';
    bankReference?: string;
    notes?: string;
  }): Promise<ApiResponse<{
    withdrawal: WithdrawalRequest;
  }>> => {
    return fetchAPI(`/admin/withdrawals/${id}/complete`, {
      method: 'PUT',
      body: JSON.stringify(paymentData)
    });
  },

  // Update admin notes
  updateNotes: (id: string, notes: string): Promise<ApiResponse<{
    withdrawal: WithdrawalRequest;
  }>> => {
    return fetchAPI(`/admin/withdrawals/${id}/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes })
    });
  },

  // Get withdrawal statistics
  getStats: (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<WithdrawalStats>> => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    return fetchAPI<WithdrawalStats>(`/admin/withdrawals/stats?${queryParams.toString()}`);
  },
};

// ============================================
// BANK VERIFICATION MANAGEMENT API
// ============================================

// Bank Verification interfaces
export interface BankVerificationEntity {
  _id: string;
  entityType: 'supplier' | 'transporter';
  entityName: string;
  ownerName?: string;
  email: string;
  mobile: string;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  bankDetails: {
    accountHolderName?: string;
    accountNumberMasked?: string;
    accountNumber?: string;
    ifscCode?: string;
    bankName?: string;
    branchName?: string;
    accountType?: string;
    isVerified: boolean;
    updatedAt?: string;
    verifiedAt?: string;
    verifiedBy?: { _id: string; name: string };
  };
  upiDetails?: {
    upiId?: string;
    isVerified?: boolean;
  };
  hasBankDetails?: boolean;
  createdAt: string;
}

export interface BankVerificationsResponse {
  verifications: BankVerificationEntity[];
  summary: {
    supplier: { count: number };
    transporter: { count: number };
    total: number;
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// Bank Verification Management API
export const BankVerificationAPI = {
  // Get all pending bank verifications
  getPendingVerifications: (params?: {
    entityType?: 'supplier' | 'transporter';
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<BankVerificationsResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.entityType) queryParams.append('entityType', params.entityType);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    return fetchAPI<BankVerificationsResponse>(`/admin/bank-verification?${queryParams.toString()}`);
  },

  // Get bank verification status for specific entity
  getVerificationStatus: (entityType: 'supplier' | 'transporter', id: string): Promise<ApiResponse<{
    entityId: string;
    entityType: string;
    entityName: string;
    email: string;
    mobile: string;
    verificationStatus: string;
    bankDetails: BankVerificationEntity['bankDetails'];
    upiDetails?: BankVerificationEntity['upiDetails'];
    hasBankDetails: boolean;
  }>> => {
    return fetchAPI(`/admin/bank-verification/${entityType}/${id}`);
  },

  // Verify (approve) bank account
  verify: (entityType: 'supplier' | 'transporter', id: string, notes?: string): Promise<ApiResponse<{
    entityId: string;
    entityType: string;
    entityName: string;
    bankDetails: BankVerificationEntity['bankDetails'];
    notes?: string;
  }>> => {
    return fetchAPI(`/admin/bank-verification/${entityType}/${id}/verify`, {
      method: 'PUT',
      body: JSON.stringify({ notes })
    });
  },

  // Reject bank account
  reject: (entityType: 'supplier' | 'transporter', id: string, reason: string): Promise<ApiResponse<{
    entityId: string;
    entityType: string;
    entityName: string;
    rejectedBankDetails: any;
    rejectionReason: string;
    rejectedAt: string;
    rejectedBy: string;
  }>> => {
    return fetchAPI(`/admin/bank-verification/${entityType}/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason })
    });
  },
};

// Invoice Management interfaces
export interface Invoice {
  _id: string;
  invoiceNumber: string;
  invoiceDate: string;
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  user: {
    _id: string;
    name: string;
    phone: string;
    email: string;
  };
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  pdfUrl: string;
  status: 'generated' | 'downloaded';
  downloadCount: number;
  lastDownloadedAt?: string;
  createdAt: string;
}

export interface InvoiceConfig {
  companyName: string;
  companyAddress: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  companyPhone: string;
  companyEmail: string;
  companyGstin: string;
  logoUrl?: string;
  termsAndConditions: string[];
  footerNote: string;
  primaryColor: string;
  bankDetails: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    ifscCode: string;
    branch: string;
  };
  upiId?: string;
}

export interface InvoiceStats {
  summary: {
    totalInvoices: number;
    totalAmount: number;
    totalDownloads: number;
    generatedCount: number;
    downloadedCount: number;
  };
  recentInvoices: Array<{
    invoiceNumber: string;
    userName: string;
    totalAmount: number;
    createdAt: string;
  }>;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Invoice Management API
export const InvoiceAPI = {
  // Get all invoices with filters
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: 'generated' | 'downloaded';
    userId?: string;
    fromDate?: string;
    toDate?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse<InvoicesResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params?.toDate) queryParams.append('toDate', params.toDate);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return fetchAPI<InvoicesResponse>(`/admin/invoices?${queryParams.toString()}`);
  },

  // Get invoice by ID
  getById: (id: string): Promise<ApiResponse<Invoice>> => {
    return fetchAPI<Invoice>(`/admin/invoices/${id}`);
  },

  // Get invoice configuration
  getConfig: (): Promise<ApiResponse<InvoiceConfig>> => {
    return fetchAPI<InvoiceConfig>('/admin/invoices/config');
  },

  // Update invoice configuration
  updateConfig: (config: Partial<InvoiceConfig>): Promise<ApiResponse<InvoiceConfig>> => {
    return fetchAPI<InvoiceConfig>('/admin/invoices/config', {
      method: 'PUT',
      body: JSON.stringify(config)
    });
  },

  // Get invoice statistics
  getStats: (): Promise<ApiResponse<InvoiceStats>> => {
    return fetchAPI<InvoiceStats>('/admin/invoices/stats');
  },

  // Regenerate invoice for an order
  regenerate: (orderId: string): Promise<ApiResponse<{
    invoiceNumber: string;
    pdfUrl: string;
    regeneratedAt: string;
  }>> => {
    return fetchAPI(`/admin/invoices/${orderId}/regenerate`, {
      method: 'POST'
    });
  },
};

// ===== FAQ + App Content (admin-managed) =====
export interface AdminFaq {
  _id: string;
  question: string;
  answer: string;
  category?: string;
  tags?: string[];
  isActive?: boolean;
  createdAt?: string;
}

export const FaqAPI = {
  getAll: (params?: { search?: string; category?: string; isActive?: boolean }) => {
    const qp: string[] = [];
    if (params?.search) qp.push(`search=${encodeURIComponent(params.search)}`);
    if (params?.category) qp.push(`category=${params.category}`);
    if (params?.isActive !== undefined) qp.push(`isActive=${params.isActive}`);
    qp.push('limit=200');
    const qs = qp.length ? `?${qp.join('&')}` : '';
    return fetchAPI<{ faqs: AdminFaq[]; totalFAQs: number }>(`/admin/faqs${qs}`);
  },
  create: (data: { question: string; answer: string; category?: string; tags?: string[] }) =>
    fetchAPI<AdminFaq>('/admin/faqs', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<AdminFaq>) =>
    fetchAPI<AdminFaq>(`/admin/faqs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string, reason?: string) =>
    fetchAPI(`/admin/faqs/${id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason || '' }) })
};

export type ContentKey = 'support_contacts' | 'privacy_policy' | 'terms' | 'status_messages';

export const ContentAPI = {
  list: () => fetchAPI<Array<{ key: ContentKey; value: any; updatedAt?: string }>>('/admin/content'),
  get: (key: ContentKey) => fetchAPI<{ key: ContentKey; value: any } | null>(`/admin/content/${key}`),
  upsert: (key: ContentKey, value: any) =>
    fetchAPI<{ key: ContentKey; value: any }>(`/admin/content/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value })
    })
};
